import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { API, formatApiError } from "@/lib/api";
import { SKILLS, skillMap, STATUS, formatDate, formatRange, groupRequests, dayLabel, romeNow, romeTodayKey } from "@/lib/constants";
import { useCategories, catChipStyle, catChipSolid } from "@/context/CategoriesContext";
import { CategoriesPanel } from "@/components/CategoryManagerDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { UnavRequestsPanel } from "@/components/UnavRequestsPanel";
import { SubcontractorMails } from "@/components/SubcontractorMails";
import { EmailFailuresPanel } from "@/components/EmailFailuresPanel";
import { DriveSyncPanel } from "@/components/DriveSyncPanel";
import { ReportsPanel } from "@/components/ReportsPanel";
import { StatementsPanel } from "@/components/StatementsPanel";
import { ManagerMessages, useUnreadMessages } from "@/components/Chat";
import {
  Radio, LogOut, Plus, CalendarDays, Users, ClipboardList, CheckCircle2, XCircle, Settings,
  Clock, ShieldCheck, Phone, Filter, Send, UserCheck, MapPin, Pencil, Trash2, HelpCircle,
  Paperclip, Upload, Download, Check, UserPlus, ChevronLeft, ChevronRight, ArrowRightLeft, KeyRound, Tags, Loader2, AlertTriangle, MessageSquare, GripVertical, X, Undo2, EyeOff, StickyNote, SplitSquareHorizontal, Hammer, FileSpreadsheet,
} from "lucide-react";
import { DndContext, closestCenter, pointerWithin, PointerSensor, TouchSensor, useSensor, useSensors, useDraggable, useDroppable } from "@dnd-kit/core";
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const statusIcon = { in_attesa: Clock, disponibile: CheckCircle2, forse: HelpCircle, non_disponibile: XCircle, confermato: ShieldCheck };
const statusDot = {
  in_attesa: "bg-amber-400", disponibile: "bg-lime-400", forse: "bg-violet-400",
  non_disponibile: "bg-rose-400", confermato: "bg-blue-600",
};

const byTech = (list) => groupRequests(list, (r) => `${r.event_id}|${r.technician_id}`);
const techCount = (list) => new Set((list || []).map((r) => r.technician_id)).size;

function DayChips({ days, onConfirm }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="day-chips">
      {days.map((r) => {
        const s = STATUS[r.status] || {};
        const can = onConfirm && r.status === "disponibile";
        return (
          <button key={r.id} type="button" disabled={!can} onClick={() => can && onConfirm(r.id)}
            data-testid={`day-chip-${r.id}`} title={can ? "Clicca per confermare questo giorno" : s.label}
            className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium ${s.color} ${can ? "cursor-pointer hover:ring-1 hover:ring-blue-400" : "cursor-default"}`}>
            <span className={`w-2 h-2 rounded-full ${statusDot[r.status]}`} />
            {dayLabel(r.date)}
            {can && <ShieldCheck className="w-3 h-3" />}
          </button>
        );
      })}
    </div>
  );
}

const todayIso = romeTodayKey;

function todoSections(requests) {
  const t = todayIso();
  const up = (requests || []).filter((r) => r.date >= t);
  return {
    confirm: byTech(up.filter((r) => r.status === "disponibile")),
    pending: byTech(up.filter((r) => r.status === "in_attesa" || r.status === "forse")),
    unread: up.filter((r) => r.status === "confermato" && r.service_reminder && !r.service_reminder_ack_at).sort((a, b) => a.date.localeCompare(b.date)),
  };
}

function DeletedEventsArchive({ onRestored }) {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/deleted-events").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const restore = async (d) => {
    if (!window.confirm(`Ricreare l'evento "${d.name}" con gli stessi dati? I tecnici NON verranno interpellati di nuovo.`)) return;
    setBusy(d.id);
    try {
      const res = await api.post(`/deleted-events/${d.id}/restore`);
      const n = res.data.attachments_relinked;
      toast.success(`Evento ripristinato${n ? ` con ${n} allegat${n > 1 ? "i" : "o"}` : ""}`);
      load();
      onRestored();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };
  if (!items) return <div className="flex items-center gap-2 text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento…</div>;
  return (
    <div className="space-y-3" data-testid="deleted-archive">
      <h2 className="font-display text-xl font-semibold">Eventi cancellati</h2>
      {items.length === 0 ? <EmptyState text="Nessun evento cancellato finora." /> : items.map((d) => (
        <div key={d.id} data-testid={`deleted-event-${d.id}`} className="rounded-xl border border-slate-800 bg-slate-900 p-4 space-y-2">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <p className="font-semibold">{d.name}</p>
              <p className="text-sm text-slate-400">{formatRange(d.start_date, d.end_date)}{d.client_name ? ` · ${d.client_name}` : ""}{d.location ? ` · ${d.location}` : ""}</p>
            </div>
            <div className="flex flex-col items-end gap-1.5">
              <p className="text-xs text-slate-500">Cancellato il {formatDate(d.deleted_at)}{d.deleted_by ? ` da ${d.deleted_by}` : ""}</p>
              {d.restored_event_id ? (
                <span data-testid={`restored-badge-${d.id}`} className="text-[11px] rounded-full border border-lime-400/50 bg-lime-400/10 text-lime-300 px-2 py-0.5">Ripristinato il {formatDate(d.restored_at)}</span>
              ) : (
                <Button size="sm" variant="outline" data-testid={`restore-event-${d.id}`} disabled={busy === d.id} onClick={() => restore(d)}
                  className="h-8 border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10">
                  {busy === d.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ArrowRightLeft className="w-4 h-4 mr-1.5" /> Ripristina evento</>}
                </Button>
              )}
            </div>
          </div>
          <p className="text-sm" data-testid={`deleted-reason-${d.id}`}><span className="text-slate-500">Motivo:</span> {d.reason || <span className="text-slate-500 italic">non indicato</span>}</p>
          <div>
            <p className="text-xs uppercase tracking-wider font-mono text-slate-500 mb-1">Tecnici avvisati ({d.technicians?.length || 0})</p>
            {(d.technicians || []).length === 0 ? <p className="text-sm text-slate-500">Nessuno</p> : (
              <div className="space-y-1">
                {d.technicians.map((t, i) => (
                  <div key={i} className="flex items-center gap-2 flex-wrap text-sm">
                    <span className="font-medium">{t.name}</span>
                    {t.days.map((x) => (
                      <span key={x.date} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${STATUS[x.status]?.color || ""}`}>
                        <span className={`w-2 h-2 rounded-full ${statusDot[x.status]}`} /> {dayLabel(x.date)} · {STATUS[x.status]?.label || x.status}
                      </span>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function TodoHead({ title, n, tone }) {
  return (
    <h3 className="font-display font-semibold flex items-center gap-2 mb-2">
      {title} <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${tone}`}>{n}</span>
    </h3>
  );
}

function TodoRow({ g, onOpenEvent, children, testid }) {
  const r = g[0];
  return (
    <div data-testid={testid} className="rounded-xl border border-slate-800 bg-slate-900 p-3.5 flex flex-col sm:flex-row sm:items-center gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => onOpenEvent(r.event_id)} className="font-semibold truncate hover:text-cyan-300" data-testid={`${testid}-open`}>{r.event_name || "Evento"}</button>
          <SkillBadge id={r.skill} />
        </div>
        <p className="text-sm text-slate-400 mt-0.5 flex items-center gap-1"><UserCheck className="w-3.5 h-3.5" /> {r.technician_name}</p>
      </div>
      {children}
    </div>
  );
}

function TodoBoard({ requests, onConfirm, onRemind, onOpenEvent }) {
  const { confirm, pending, unread } = todoSections(requests);
  if (!confirm.length && !pending.length && !unread.length) {
    return <EmptyState text="Tutto in ordine: nessuna conferma, sollecito o promemoria in sospeso." />;
  }
  return (
    <div className="space-y-6" data-testid="todo-board">
      <h2 className="font-display text-xl font-semibold">Da fare</h2>
      {confirm.length > 0 && (
        <section data-testid="todo-confirm">
          <TodoHead title="Disponibili da confermare" n={confirm.length} tone="bg-lime-400/15 text-lime-300" />
          <div className="grid gap-2">
            {confirm.map((g) => (
              <TodoRow key={`${g[0].event_id}-${g[0].technician_id}`} g={g} onOpenEvent={onOpenEvent} testid={`todo-confirm-${g[0].id}`}>
                <div className="flex flex-col gap-1 sm:items-end">
                  <span className="text-[11px] text-slate-500">Clicca un giorno per confermarlo</span>
                  <DayChips days={g} onConfirm={onConfirm} />
                  {g.filter((d) => d.response_message).map((d) => <p key={d.id} data-testid={`todo-confirm-note-${d.id}`} className="text-xs text-amber-200 italic">Nota ({dayLabel(d.date)}): {d.response_message}</p>)}
                </div>
              </TodoRow>
            ))}
          </div>
        </section>
      )}
      {pending.length > 0 && (
        <section data-testid="todo-pending">
          <TodoHead title="In attesa o Forse" n={pending.length} tone="bg-amber-500/15 text-amber-300" />
          <div className="grid gap-2">
            {pending.map((g) => {
              const p = g[0];
              const msg = g.find((x) => x.status === "forse" && x.response_message);
              return (
                <TodoRow key={`${p.event_id}-${p.technician_id}`} g={g} onOpenEvent={onOpenEvent} testid={`todo-pending-${p.id}`}>
                  <div className="flex flex-col gap-1.5 sm:items-end">
                    <DayChips days={g} />
                    {msg && <p className="text-xs text-violet-300 italic">Motivo “Forse”: {msg.response_message}</p>}
                    <div className="flex items-center gap-2">
                      <span data-testid={`todo-remind-count-${p.id}`} className="text-[11px] text-slate-500">{p.reminder_count || 0}× sollecitato</span>
                      <Button size="sm" variant="outline" data-testid={`todo-remind-${p.id}`} onClick={() => onRemind(p.id)}
                        className="h-8 border-amber-500/40 text-amber-300 hover:bg-amber-500/10"><Send className="w-4 h-4 mr-1.5" /> Sollecita</Button>
                    </div>
                  </div>
                </TodoRow>
              );
            })}
          </div>
        </section>
      )}
      {unread.length > 0 && (
        <section data-testid="todo-unread">
          <TodoHead title="Promemoria non letti" n={unread.length} tone="bg-slate-700 text-slate-200" />
          <div className="grid gap-2">
            {unread.map((r) => (
              <TodoRow key={r.id} g={[r]} onOpenEvent={onOpenEvent} testid={`todo-unread-${r.id}`}>
                <span className="inline-flex items-center gap-1.5 text-sm text-slate-300"><Clock className="w-4 h-4 text-slate-400" /> servizio {r.service_reminder.split(":")[1]} · {dayLabel(r.date)}</span>
              </TodoRow>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const s = STATUS[status];
  const Icon = statusIcon[status];
  return (
    <span data-testid={`status-${status}`} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${s.color}`}>
      <Icon className="w-3.5 h-3.5" /> {s.label}
    </span>
  );
}

function SkillBadge({ id }) {
  const s = skillMap[id];
  if (!s)
    return (
      <span className="inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium bg-slate-700/40 text-slate-300 border-slate-600">
        Tutte le skill
      </span>
    );
  return (
    <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium ${s.color}`}>
      <s.icon className="w-3 h-3" /> {s.label}
    </span>
  );
}

function CategoryBadge({ id }) {
  const { byName } = useCategories();
  if (!id) return null;
  const c = byName[id];
  return <span data-testid={`category-badge-${id}`} className="inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-bold" style={catChipSolid(c?.color, c?.text_color)}>{id}</span>;
}

export default function ManagerDashboard() {
  const { user, logout } = useAuth();
  const [events, setEvents] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [requests, setRequests] = useState([]);
  const [skillFilter, setSkillFilter] = useState("all");
  const [detailEvent, setDetailEvent] = useState(null);
  const [detailTech, setDetailTech] = useState(null);
  const [tab, setTab] = useState("events");
  const [eventsView, setEventsView] = useState("calendario");
  const [calEventDate, setCalEventDate] = useState(null);
  const [calEventOpen, setCalEventOpen] = useState(false);
  const [hideAllest, setHideAllest] = useState(() => localStorage.getItem("hide-allestimenti") === "1");
  useEffect(() => { localStorage.setItem("hide-allestimenti", hideAllest ? "1" : "0"); }, [hideAllest]);

  const [collaborators, setCollaborators] = useState([]);
  const [unavail, setUnavail] = useState([]);
  const [pendingN, setPendingN] = useState(0);
  const [undoN, setUndoN] = useState(0);
  const undoable = (fn, createdOf) => async (...args) => {
    let id = null;
    try {
      const s = sessionStorage.getItem("avail-week"); const t = romeNow();
      const ws = s ? new Date(s) : addDays(t, -((t.getDay() + 6) % 7));
      id = (await api.post(`/undo/snapshot?sid=${UNDO_SID}`, { start: dayKey(ws), end: dayKey(addDays(ws, 6)) })).data.id;
    } catch { id = null; }
    try { await fn(...args); } finally {
      if (id) { try { const r = await api.post(`/undo/commit?sid=${UNDO_SID}`, { id, created_event_id: createdOf ? createdOf(...args) : null }); setUndoN(r.data.count); } catch { /* ignore */ } }
    }
  };
  const doUndo = async () => {
    try { const r = await api.post(`/undo?sid=${UNDO_SID}`); setUndoN(r.data.count); toast.success(r.data.skipped ? "Modifica annullata (alcune risposte arrivate nel frattempo sono state mantenute)" : "Ultima modifica annullata"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    loadAll(true);
  };

  const loadAll = async (silent = false) => {
    const res = await Promise.allSettled([
      api.get("/events"), api.get("/technicians"), api.get("/requests"), api.get("/collaborators"), api.get("/unavailability"),
    ]);
    [setEvents, setTechnicians, setRequests, setCollaborators, setUnavail].forEach((set, i) => {
      if (res[i].status === "fulfilled") set(res[i].value.data);
    });
    const failed = res.find((r) => r.status === "rejected");
    api.get("/notifications/pending").then((r) => setPendingN(r.data.count)).catch(() => {});
    if (failed && !silent) toast.error(formatApiError(failed.reason?.response?.data?.detail));
  };

  useEffect(() => {
    loadAll();
    api.delete(`/undo?sid=${UNDO_SID}`).catch(() => {});
    const iv = setInterval(() => loadAll(true), 12000);
    const onVis = () => { if (document.visibilityState === "visible") loadAll(true); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", onVis); window.removeEventListener("focus", onVis); };
    // eslint-disable-next-line
  }, []);

  const filteredTechs = useMemo(
    () => (skillFilter === "all" ? technicians : technicians.filter((t) => t.skills?.includes(skillFilter))),
    [technicians, skillFilter]
  );

  const stats = useMemo(() => ({
    inAttesa: byTech(requests).filter((g) => g.some((r) => r.status === "in_attesa")).length,
    disponibile: requests.filter((r) => r.status === "disponibile").length,
    confermato: requests.filter((r) => r.status === "confermato").length,
    tecnici: technicians.filter((t) => !t.videomaker && !t.warehouse && !t.subcontractor).length,
    subappaltatori: technicians.filter((t) => t.subcontractor).length,
    videomaker: technicians.filter((t) => t.videomaker).length,
    magazzinieri: technicians.filter((t) => t.warehouse && !t.videomaker).length,
  }), [requests, technicians]);
  const unreadMsgs = useUnreadMessages();
  const [missingCount, setMissingCount] = useState(0);
  const loadMissing = () => api.get("/reports/missing").then((r) => setMissingCount(r.data.reduce((n, ev) => n + ev.technicians.filter((t) => !t.waived).length, 0))).catch(() => {});
  useEffect(() => { loadMissing(); const iv = setInterval(loadMissing, 60000); return () => clearInterval(iv); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps
  const todoCount = useMemo(() => { const s = todoSections(requests); return s.confirm.length + s.pending.length + s.unread.length; }, [requests]);

  const confirmService = async (id) => {
    try {
      await api.put(`/requests/${id}/confirm?draft=true`);
      toast.success("Confermato (notifica in attesa di invio)");
      loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remind = async (id) => {
    try {
      await api.post(`/requests/${id}/remind`);
      toast.success("Sollecito inviato via email");
      loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const clientConfirm = async (id) => {
    try {
      await api.put(`/events/${id}/client-confirm`);
      toast.success("Conferma cliente registrata");
      loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const resendPassword = async (t) => {
    if (!window.confirm(`Reimpostare la password di ${t.name} e inviargliela via email? La password attuale smetterà di funzionare.`)) return;
    try {
      const res = await api.post(`/technicians/${t.id}/reset-password`);
      if (res.data.email_sent) toast.success(`Nuova password inviata via email a ${t.email}`);
      else toast.warning("Password reimpostata, ma l'email non è partita. Riprova più tardi.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const deleteCollaborator = async (c) => {
    if (!window.confirm(`Eliminare il collaboratore ${c.name}?`)) return;
    try { await api.delete(`/collaborators/${c.id}`); toast.success("Collaboratore eliminato"); loadAll(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openDetail = (e) => setDetailEvent(e);
  const detailRequests = useMemo(
    () => (detailEvent ? requests.filter((r) => r.event_id === detailEvent.id) : []),
    [detailEvent, requests]
  );

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="sticky top-0 z-30 border-b border-slate-800 bg-slate-950/90 backdrop-blur-md" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="max-w-7xl mx-auto px-3 sm:px-6 h-16 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <img src="/logo.webp" alt="GCAVCOM" className="h-6 sm:h-8 w-auto min-w-0" />
            <div className="shrink-0">
              <Badge className="bg-cyan-500/15 text-cyan-300 border-cyan-500/30 text-[9px] sm:text-[10px] px-1.5 sm:px-2.5">MANAGER</Badge>
            </div>
          </div>
          <div className="flex items-center gap-0.5 sm:gap-3 shrink-0">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" data-testid="manage-menu-btn"
                  className="text-slate-400 hover:text-white hover:bg-slate-800">
                  <Settings className="w-4 h-4 sm:mr-1.5" /> <span className="hidden sm:inline">Gestione</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-slate-900 border-slate-700 text-slate-100">
                <DropdownMenuItem data-testid="menu-technicians" onClick={() => setTab("technicians")} className="cursor-pointer focus:bg-slate-800 focus:text-white">
                  <Users className="w-4 h-4 mr-2 text-cyan-400" /> Tecnici
                  {stats.tecnici > 0 && <span className="ml-auto rounded-full bg-cyan-500/20 text-cyan-300 text-[10px] font-bold px-1.5">{stats.tecnici}</span>}
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-subcontractors" onClick={() => setTab("subcontractors")} className="cursor-pointer focus:bg-slate-800 focus:text-white">
                  <Users className="w-4 h-4 mr-2 text-teal-400" /> Subappaltatori
                  {stats.subappaltatori > 0 && <span className="ml-auto rounded-full bg-teal-500/20 text-teal-300 text-[10px] font-bold px-1.5">{stats.subappaltatori}</span>}
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-videomakers" onClick={() => setTab("videomakers")} className="cursor-pointer focus:bg-slate-800 focus:text-white">
                  <Users className="w-4 h-4 mr-2 text-pink-400" /> Videomaker
                  {stats.videomaker > 0 && <span className="ml-auto rounded-full bg-pink-500/20 text-pink-300 text-[10px] font-bold px-1.5">{stats.videomaker}</span>}
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-warehouse" onClick={() => setTab("warehouse")} className="cursor-pointer focus:bg-slate-800 focus:text-white">
                  <Users className="w-4 h-4 mr-2 text-amber-400" /> Magazzinieri
                  {stats.magazzinieri > 0 && <span className="ml-auto rounded-full bg-amber-500/20 text-amber-300 text-[10px] font-bold px-1.5">{stats.magazzinieri}</span>}
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-categories" onClick={() => setTab("categories")} className="cursor-pointer focus:bg-slate-800 focus:text-white">
                  <Tags className="w-4 h-4 mr-2 text-cyan-400" /> Categorie
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-reports" onClick={() => setTab("reports")} className="cursor-pointer focus:bg-slate-800 focus:text-white">
                  <ClipboardList className="w-4 h-4 mr-2 text-amber-400" /> Report eventi
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-statements" onClick={() => setTab("statements")} className="cursor-pointer focus:bg-slate-800 focus:text-white">
                  <FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-400" /> Resoconti mensili
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-archive" onClick={() => setTab("archive")} className="cursor-pointer focus:bg-slate-800 focus:text-white">
                  <Trash2 className="w-4 h-4 mr-2 text-rose-400" /> Eventi cancellati
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <ChangePasswordDialog trigger={
              <Button variant="ghost" size="sm" data-testid="open-change-password-btn"
                className="text-slate-400 hover:text-white hover:bg-slate-800">
                <KeyRound className="w-4 h-4 sm:mr-1.5" /> <span className="hidden sm:inline">Password</span>
              </Button>
            } />
            <Button variant="ghost" size="sm" onClick={logout} data-testid="logout-btn"
              className="text-slate-400 hover:text-white hover:bg-slate-800">
              <LogOut className="w-4 h-4 sm:mr-1.5" /> <span className="hidden sm:inline">Esci</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="main-tabs" style={{ top: "calc(env(safe-area-inset-top) + 65px)" }} className="sticky z-20 w-full grid grid-cols-4 gap-1 sm:gap-3 text-[11px] sm:text-sm h-auto mb-6 bg-slate-950/95 backdrop-blur-md py-2 -mx-1 px-1 rounded-none">
            <TabsTrigger value="availability" data-testid="tab-availability" className="justify-center py-2.5 px-1 rounded-lg border border-slate-800 bg-slate-900 data-[state=active]:bg-cyan-500/15 data-[state=active]:text-cyan-200 data-[state=active]:border-cyan-500/40"><CheckCircle2 className="w-4 h-4 mr-1.5 hidden sm:inline" /> Disponibilità</TabsTrigger>
            <TabsTrigger value="events" data-testid="tab-events" className="justify-center py-2.5 rounded-lg border border-slate-800 bg-slate-900 data-[state=active]:bg-cyan-500/15 data-[state=active]:text-cyan-200 data-[state=active]:border-cyan-500/40"><CalendarDays className="w-4 h-4 mr-1.5 hidden sm:inline" /> Eventi</TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages" className="justify-center py-2.5 rounded-lg border border-slate-800 bg-slate-900 data-[state=active]:bg-cyan-500/15 data-[state=active]:text-cyan-200 data-[state=active]:border-cyan-500/40"><MessageSquare className="w-4 h-4 mr-1.5 hidden sm:inline" /> Chat
              {unreadMsgs > 0 && <span data-testid="messages-unread-count" className="ml-1.5 rounded-full bg-amber-500/20 text-amber-300 text-[10px] font-bold px-1.5 py-0.5">{unreadMsgs}</span>}
            </TabsTrigger>
            <TabsTrigger value="reports" data-testid="tab-reports" className="justify-center py-2.5 px-1 rounded-lg border border-slate-800 bg-slate-900 data-[state=active]:bg-cyan-500/15 data-[state=active]:text-cyan-200 data-[state=active]:border-cyan-500/40"><ClipboardList className="w-4 h-4 mr-1.5 hidden sm:inline" /> Report
              {missingCount > 0 && <span data-testid="reports-missing-count" className="ml-1 rounded-full bg-rose-500/20 text-rose-300 text-[10px] font-bold px-1.5 py-0.5">{missingCount}</span>}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="messages">
            {tab === "messages" && <ManagerMessages events={events} onOpenEvent={(id) => { const e = events.find((x) => x.id === id); if (e) setDetailEvent(e); else toast.error("Evento non più disponibile"); }} />}
          </TabsContent>

          {/* REQUESTS */}

          {/* TECHNICIANS */}
          <TabsContent value="technicians" className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h2 className="font-display text-xl font-semibold">Lista Tecnici</h2>
              <div className="flex items-center gap-2 flex-wrap">
                <NewTechnicianDialog onDone={loadAll} />
                <Filter className="w-4 h-4 text-slate-400" />
                <Select value={skillFilter} onValueChange={setSkillFilter}>
                  <SelectTrigger data-testid="skill-filter-select" className="w-[180px] bg-slate-900 border-slate-700">
                    <SelectValue placeholder="Filtra per skill" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                    <SelectItem value="all">Tutte le skill</SelectItem>
                    {SKILLS.map((s) => (
                      <SelectItem key={s.id} value={s.id} data-testid={`filter-option-${s.id}`}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
              <h3 className="font-display font-semibold mb-1 flex items-center gap-2"><UserPlus className="w-4 h-4 text-cyan-400" /> Collaboratori (sola lettura)</h3>
              <p className="text-xs text-slate-500 mb-3">Vedono solo il calendario eventi, le persone coinvolte e possono sollecitare. Creane uno con "Nuovo Tecnico" → scegli "Collaboratore".</p>
              {collaborators.length === 0 ? (
                <p className="text-sm text-slate-500">Nessun collaboratore.</p>
              ) : (
                <div className="space-y-2">
                  {collaborators.map((c) => (
                    <div key={c.id} data-testid={`collaborator-row-${c.id}`} className="flex items-center justify-between gap-2 flex-wrap rounded-lg border border-slate-800 bg-slate-800/40 p-2.5">
                      <div className="min-w-0"><p className="font-medium truncate">{c.name}</p><p className="text-xs text-slate-500 truncate">{c.email}</p></div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" data-testid={`collab-reset-${c.id}`} onClick={() => resendPassword(c)} className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10 h-8"><KeyRound className="w-4 h-4 mr-1" /> Password</Button>
                        <Button size="sm" variant="outline" data-testid={`collab-delete-${c.id}`} onClick={() => deleteCollaborator(c)} className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10 h-8"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <PeopleTable list={filteredTechs.filter((t) => !t.videomaker && !t.warehouse && !t.subcontractor)} empty="Nessun tecnico con questa skill." onOpen={setDetailTech} onResend={resendPassword} />
          </TabsContent>

          <TabsContent value="subcontractors" className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h2 className="font-display text-xl font-semibold">Lista Subappaltatori</h2>
              <NewTechnicianDialog onDone={loadAll} subcontractor />
            </div>
            <p className="text-xs text-slate-500">Non hanno l'app e non ricevono notifiche automatiche. Li assegni tu dal dettaglio evento (come i dipendenti), contano come tecnici e, una volta confermati, gli scrivi tu l'email dal dettaglio evento.</p>
            <PeopleTable list={technicians.filter((t) => t.subcontractor)} empty="Nessun subappaltatore. Creane uno con “Nuovo Subappaltatore”." onOpen={setDetailTech} onResend={resendPassword} />
          </TabsContent>

          {/* VIDEOMAKERS */}
          <TabsContent value="warehouse" className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h2 className="font-display text-xl font-semibold">Lista Magazzinieri</h2>
              <NewTechnicianDialog onDone={loadAll} warehouse />
            </div>
            <p className="text-xs text-slate-500">Stanno in fondo alla tabella Disponibilità (prima dei videomaker), si interpellano dalla tabella e non contano nei tecnici previsti.</p>
            <PeopleTable list={technicians.filter((t) => t.warehouse && !t.videomaker)} empty="Nessun magazziniere. Creane uno con “Nuovo Magazziniere”." onOpen={setDetailTech} onResend={resendPassword} />
          </TabsContent>

          <TabsContent value="videomakers" className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h2 className="font-display text-xl font-semibold">Lista Videomaker</h2>
              <NewTechnicianDialog onDone={loadAll} videomaker />
            </div>
            <p className="text-xs text-slate-500">Ricevono richieste e conferme come i tecnici, stanno in fondo alla tabella Disponibilità e non contano nei tecnici previsti.</p>
            <PeopleTable list={technicians.filter((t) => t.videomaker)} empty="Nessun videomaker. Creane uno con “Nuovo Videomaker”." onOpen={setDetailTech} onResend={resendPassword} />
          </TabsContent>

          {/* CATEGORIES */}
          <TabsContent value="categories" className="space-y-4">
            <CategoriesPanel />
          </TabsContent>

          <TabsContent value="reports" className="space-y-4">
            {tab === "reports" && <ReportsPanel onChange={loadMissing} />}
          </TabsContent>

          <TabsContent value="statements" className="space-y-4">
            {tab === "statements" && <StatementsPanel />}
          </TabsContent>

          <TabsContent value="archive" className="space-y-4">
            {tab === "archive" && <DeletedEventsArchive onRestored={() => loadAll(true)} />}
          </TabsContent>

          {/* AVAILABILITY */}
          <TabsContent value="availability" className="space-y-4">
            <h2 className="font-display text-xl font-semibold">Disponibilità Settimanale</h2>
            <UnavRequestsPanel onChanged={() => loadAll(true)} />
            <EmailFailuresPanel />
            <DriveSyncPanel />
            <AvailabilityBoard technicians={technicians} requests={requests} events={events} unavail={unavail} onRemind={remind}
              onReorder={async (ids) => { await api.put("/technicians/order", { ids }); loadAll(true); }}
              onSwap={undoable(async (id, body) => { await api.post(`/requests/${id}/swap`, body); await loadAll(true); })}
              onAskGeneric={undoable(async (tech, d) => {
                try { const r = await api.post("/availability", { start_date: d, technician_ids: [tech.id], draft: true }); toast[r.data.created ? "success" : "error"](r.data.created ? `Richiesta di disponibilità per ${tech.name} (parte con Invia notifiche)` : "Già richiesta per questa data"); }
                catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                loadAll(true);
              })}
              onMoveEvent={undoable(async (r, ev) => {
                try { await api.put(`/requests/${r.id}/move`, { event_id: ev.id, silent: true }); toast.success(r.event_id ? `${r.technician_name} spostato su "${ev.name}" (nessun avviso)` : `${r.technician_name} assegnato a "${ev.name}" (nessun avviso)`); }
                catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                loadAll(true);
              })}
              pendingCount={pendingN} undoCount={undoN} onUndo={doUndo}
              onFlush={async () => {
                try { const r = await api.post("/notifications/flush"); toast.success(`Notifiche inviate a ${r.data.sent} tecnico/i`); setUndoN(0); }
                catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                loadAll(true);
              }}
              onRemoveRequest={undoable(async (r) => {
                try { await api.delete(`/requests/${r.id}?draft=true`); toast.success(`${r.technician_name} tolto`); }
                catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                loadAll(true);
              })}
              onDeleteEvent={undoable(async (r) => {
                try { await api.delete(`/events/${r.event_id}?force=true&draft=true`); toast.success(`Evento "${r.event_name}" eliminato`); }
                catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                loadAll(true);
              })}
              onUnmarkUnavail={undoable(async (uid, d) => {
                try { await api.delete(`/unavailability/${uid}?day=${d}`); toast.success("Non disponibilità tolta"); }
                catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                loadAll(true);
              })}
              onMarkUnavail={undoable(async (tech, d) => {
                try { await api.post("/unavailability", { tech_id: tech.id, start: d, end: d }); toast.success(`${tech.name} segnato non disponibile`); }
                catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                loadAll(true);
              })}
              onMarkUnavailMany={undoable(async (ranges, n) => {
                try {
                  for (const r of ranges) await api.post("/unavailability", r);
                  toast.success(`${n} giorn${n === 1 ? "o segnato" : "i segnati"} non disponibil${n === 1 ? "e" : "i"}`);
                } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                loadAll(true);
              })}
              onToUnavail={undoable(async (r) => {
                try { await api.put(`/requests/${r.id}/to-unavailable`); toast.success(`${r.technician_name}: ora Non disponibile il ${dayLabel(r.date)}`); }
                catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                loadAll(true);
              })}
              onDetach={undoable(async (r) => {
                try { await api.put(`/requests/${r.id}/detach`); toast.success(r.status === "in_attesa" ? `${r.technician_name}: ora richiesta senza evento` : `${r.technician_name}: ora disponibilità generica`); }
                catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                loadAll(true);
              })}
              onToggleSupervisor={undoable(async (r) => {
                try { await api.put(`/requests/${r.id}/supervisor`, { value: !r.supervisor }); toast.success(r.supervisor ? `${r.technician_name} non è più supervisore` : `${r.technician_name} è supervisore (non conta nei previsti)`); }
                catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                loadAll(true);
              })}
              onSetSlot={undoable(async (r, start, end) => {
                try {
                  await api.put(`/requests/${r.id}/slot`, { slot_start: start || null, slot_end: end || null });
                  toast.success(start ? `Orario ${start}–${end} salvato per ${r.technician_name}${r.status === "confermato" ? " (avviso in attesa di invio)" : ""}` : `Orario tolto a ${r.technician_name}`);
                } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                loadAll(true);
              })}
              onConfirm={undoable(async (id) => {
                try { await api.put(`/requests/${id}/confirm?draft=true`); toast.success("Confermato (notifica in attesa di invio)"); }
                catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                loadAll(true);
              })}
              onCellEvent={undoable(async (tech, ev, dates, supervisor = false, isNew = false, slot = null) => {
                try {
                  const sl = slot ? { slot_start: slot.start, slot_end: slot.end } : {};
                  const r = tech.employee ? await api.post(`/events/${ev.id}/assign`, { technician_ids: [tech.id], dates: dates || [], supervisor, ...sl })
                    : await api.post("/requests/bulk", { event_id: ev.id, technician_ids: [tech.id], dates: dates || [], draft: true, supervisor, ...sl });
                  if (!r.data.created) toast.error(slot ? `${tech.name} ha già un servizio che si sovrappone a ${slot.start}–${slot.end}` : `${tech.name} è già su questo evento o già disponibile/confermato altrove`);
                  else toast.success(slot ? `Partizione ${slot.start}–${slot.end} aggiunta a ${tech.name} (se il giorno era confermato, è già confermata)` : tech.employee ? `${tech.name} messo come Disponibile` : `${tech.name} aggiunto (richiesta in attesa di invio)`);
                } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                loadAll(true);
              }, (tech, ev, dates, supervisor, isNew) => (isNew ? ev.id : null))} />
          </TabsContent>

          {/* EVENTS */}
          <TabsContent value="events" className="space-y-4">
            <NewEventDialog onDone={loadAll} open={calEventOpen} onOpenChange={setCalEventOpen} defaultDate={calEventDate} />
            <EventsCalendar events={hideAllest ? events.filter((e) => !/allestiment/i.test(e.category || "")) : events} requests={requests} onOpen={openDetail}
              onOpenWeek={(d, e) => { sessionStorage.setItem("avail-highlight", e.id); sessionStorage.setItem("avail-week", addDays(d, -((d.getDay() + 6) % 7)).toISOString()); setTab("availability"); window.scrollTo(0, 0); }}
              action={<>
                <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer select-none" data-testid="hide-allestimenti-label">
                  <Switch checked={hideAllest} onCheckedChange={setHideAllest} data-testid="hide-allestimenti-switch" className="data-[state=checked]:bg-red-500" />
                  Nascondi Allestimenti
                </label>
                <NewEventDialog onDone={loadAll} />
              </>}
              onAddOnDate={(s, e) => { setCalEventDate({ start: s, end: e }); setCalEventOpen(true); }} />
          </TabsContent>
        </Tabs>
      </main>

      {detailEvent && (
        <EventDetailDialog
          event={events.find((e) => e.id === detailEvent.id) || detailEvent}
          eventRequests={detailRequests}
          allRequests={requests}
          unavail={unavail}
          technicians={technicians}
          events={events}
          onOpenTech={(tid) => { const t = technicians.find((x) => x.id === tid); if (t) setDetailTech(t); }}
          onClose={() => setDetailEvent(null)}
          onDone={loadAll}
          onConfirmService={confirmService}
          onClientConfirm={clientConfirm}
        />
      )}
      {detailTech && (
        <TechnicianDetailDialog
          technician={detailTech}
          requests={requests.filter((r) => r.technician_id === detailTech.id)}
          unavail={unavail.filter((u) => u.tech_id === detailTech.id)}
          onUnavailChanged={() => loadAll(true)}
          onClose={() => setDetailTech(null)}
          onDeleted={loadAll}
        />
      )}
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div className="rounded-xl border border-dashed border-slate-800 bg-slate-900/50 p-10 text-center">
      <p className="text-slate-500 text-sm">{text}</p>
    </div>
  );
}

function EventCard({ e, count, onOpen }) {
  return (
    <button data-testid={`event-card-${e.id}`} onClick={() => onOpen(e)}
      className="text-left rounded-xl border border-slate-800 bg-slate-900 p-4 hover:border-cyan-500/50 transition-colors">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-semibold">{e.name}</p>
          <p className="text-sm text-slate-400">{e.client_name}</p>
          {e.category && <div className="mt-1"><CategoryBadge id={e.category} /></div>}
        </div>
        {e.client_confirmed ? (
          <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-xs">Cliente OK</Badge>
        ) : (
          <Badge className="bg-slate-700/40 text-slate-400 border-slate-600 text-xs">In sospeso</Badge>
        )}
      </div>
      <div className="flex items-center gap-3 text-sm text-slate-400 mt-3 flex-wrap">
        <span className="flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> {formatRange(e.start_date, e.end_date)}</span>
        {e.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {e.location}</span>}
      </div>
      <div className="flex items-center gap-2 mt-3 text-xs text-cyan-300">
        <Users className="w-3.5 h-3.5" /> {count} tecnico/i interpellato/i — tocca per gestire
      </div>
    </button>
  );
}

const exportPdf = async (period, dateStr, format = "pdf") => {
  try {
    const res = await api.get(`/export/availability?period=${period}&date=${dateStr}&format=${format}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${period === "month" ? "disponibilita-mese" : "disponibilita-settimana"}.${format}`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success(format === "csv" ? "File per Fogli Google scaricato" : "PDF generato");
  } catch (err) {
    toast.error(format === "csv" ? "Export file non riuscito" : "Export PDF non riuscito");
  }
};

function SortableRow({ id, enabled, name, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled: !enabled });
  const style = { transform: CSS.Transform.toString(transform), transition, position: "relative", zIndex: isDragging ? 30 : undefined };
  return (
    <tr ref={setNodeRef} style={style} data-testid={`avail-row-${id}`} className={`odd:bg-slate-900/40 even:bg-slate-900/20 ${isDragging ? "opacity-80 shadow-lg shadow-cyan-500/20" : ""}`}>
      <td className="sticky left-0 z-10 bg-slate-900 px-3 py-2 font-medium text-slate-100 border-b border-slate-800 min-w-[140px]">
        <div className="flex items-center gap-1.5">
          {enabled && (
            <button {...attributes} {...listeners} data-testid={`avail-drag-${id}`} title="Trascina per spostare"
              className="touch-none cursor-grab active:cursor-grabbing text-slate-500 hover:text-cyan-300 -ml-1">
              <GripVertical className="w-4 h-4" />
            </button>
          )}
          <span>{name}</span>
        </div>
      </td>
      {children}
    </tr>
  );
}

function DropCell({ id, enabled, className, children, onClick, testId, style }) {
  const { setNodeRef, isOver } = useDroppable({ id, disabled: !enabled });
  return <td ref={setNodeRef} onClick={onClick} data-testid={testId} style={style} className={`${className} ${isOver ? "ring-2 ring-inset ring-cyan-400" : ""}`}>{children}</td>;
}

function SlotEditor({ r, onSave }) {
  const [s, setS] = useState(r.slot_start || "");
  const [e, setE] = useState(r.slot_end || "");
  const ok = /^\d\d:\d\d$/.test(s) && /^\d\d:\d\d$/.test(e) && s < e;
  return (
    <div className="rounded-lg border border-slate-700 p-2.5 space-y-1.5" data-testid="avail-slot-editor">
      <Label className="text-xs text-slate-400 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Orario della persona in questo giorno</Label>
      <div className="flex flex-wrap items-center gap-2">
        <Input type="time" data-testid="avail-slot-start" value={s} onChange={(x) => setS(x.target.value)} className="w-28 bg-slate-800 border-slate-700" />
        <span className="text-slate-500">–</span>
        <Input type="time" data-testid="avail-slot-end" value={e} onChange={(x) => setE(x.target.value)} className="w-28 bg-slate-800 border-slate-700" />
        <Button size="sm" data-testid="avail-slot-save" disabled={!ok} onClick={() => onSave(s, e)} className="bg-cyan-500 hover:bg-cyan-600 text-slate-950 font-semibold">Salva orario</Button>
        {r.slot_start && <Button size="sm" variant="ghost" data-testid="avail-slot-clear" onClick={() => onSave("", "")} className="text-slate-400 hover:text-red-300">Togli orario</Button>}
      </div>
      {(s || e) && !ok && <p className="text-xs text-amber-300" data-testid="avail-slot-hint">L'inizio deve essere prima della fine.</p>}
    </div>
  );
}

function DragItem({ it, enabled, onClick, allest = false }) {
  const drag = useDraggable({ id: `req:${it.id}`, disabled: !enabled });
  const drop = useDroppable({ id: `drop:${it.id}`, disabled: !enabled });
  const style = drag.transform ? { transform: CSS.Translate.toString(drag.transform), position: "relative", zIndex: 40, backgroundColor: it.bg } : it.bg ? { backgroundColor: it.bg } : undefined;
  return (
    <span ref={(n) => { drag.setNodeRef(n); drop.setNodeRef(n); }} style={style} {...drag.listeners} {...drag.attributes}
      data-testid={`avail-cell-${it.id}`} onClick={onClick} title={it.note ? `Nota: ${it.note}` : onClick ? (it.status === "unavail" ? "Tocca per togliere la non disponibilità" : "Tocca per le azioni, trascina per scambiare") : undefined}
      className={`break-words rounded px-1 ${it.cls} ${it.generic ? "italic" : ""} ${it.draft ? "outline outline-1 outline-dashed outline-amber-400/70" : ""} ${enabled ? "cursor-grab touch-none hover:bg-white/10" : ""} ${onClick ? "underline decoration-dotted underline-offset-2" : ""} ${drop.isOver ? "ring-2 ring-cyan-300" : ""} ${drag.isDragging ? "opacity-70 shadow-lg bg-slate-800" : ""}`}>
      {allest && <Hammer data-testid={`avail-allest-${it.id}`} className="inline w-3 h-3 mr-0.5 -mt-0.5" aria-label="Allestimento" />}{it.text}{it.note && <StickyNote data-testid={`avail-note-${it.id}`} className="inline w-3 h-3 ml-0.5 -mt-0.5 text-amber-300" aria-label={`Nota: ${it.note}`} />}{it.sup && <span data-testid={`avail-sup-tag-${it.id}`} className="block text-[9px] font-semibold uppercase tracking-wide opacity-90">supervisore</span>}{it.draft && <span className="ml-1 text-[9px] font-semibold text-amber-300">bozza</span>}
    </span>
  );
}

export function AvailabilityBoard({ technicians, requests, events, unavail = [], onRemind, onReorder, onSwap, onMoveEvent, onAskGeneric, onDetach, onToUnavail, undoCount = 0, onUndo, onCellEvent, onConfirm, onMarkUnavail, onMarkUnavailMany, onToggleSupervisor, onSetSlot, onUnmarkUnavail, onRemoveRequest, onDeleteEvent, pendingCount = 0, onFlush, sheetExport = false, onCellComment }) {
  const [danger, setDanger] = useState(null);
  const [selMode, setSelMode] = useState(false);
  const [sel, setSel] = useState({});
  const [selBusy, setSelBusy] = useState(false);
  const selN = Object.keys(sel).length;
  const toggleSel = (techId, date) => setSel((s) => { const k = `${techId}|${date}`; const n = { ...s }; if (n[k]) delete n[k]; else n[k] = true; return n; });
  const exitSel = () => { setSelMode(false); setSel({}); };
  const saveSel = async () => {
    const by = {};
    Object.keys(sel).forEach((k) => { const [tid, d] = k.split("|"); (by[tid] = by[tid] || []).push(d); });
    const ranges = [];
    Object.entries(by).forEach(([tid, ds]) => {
      ds.sort().forEach((d) => {
        const last = ranges[ranges.length - 1];
        if (last && last.tech_id === tid && dayKey(addDays(new Date(`${last.end}T12:00:00`), 1)) === d) last.end = d;
        else ranges.push({ tech_id: tid, start: d, end: d });
      });
    });
    setSelBusy(true);
    try { await onMarkUnavailMany(ranges, selN); exitSel(); } finally { setSelBusy(false); }
  };
  const [flushing, setFlushing] = useState(false);
  const [confirmAsk, setConfirmAsk] = useState(null);
  const [undoing, setUndoing] = useState(false);
  const moveTargets = confirmAsk ? (events || []).filter((e) => e.id !== confirmAsk.event_id && e.start_date <= confirmAsk.date && (e.end_date || e.start_date) >= confirmAsk.date) : [];
  const [cellNew, setCellNew] = useState(null);
  const [cellPick, setCellPick] = useState(null);
  const [slot, setSlot] = useState({ start: "", end: "" });
  const slotOk = /^\d\d:\d\d$/.test(slot.start) && /^\d\d:\d\d$/.test(slot.end) && slot.start < slot.end;
  const openPartition = (tech, date) => { setSlot({ start: "", end: "" }); setCellPick({ tech, date, partition: true }); };
  const [unavAsk, setUnavAsk] = useState(null);
  const [supMode, setSupMode] = useState(false);
  const dayEvents = cellPick ? (events || []).filter((e) => e.start_date <= cellPick.date && cellPick.date <= (e.end_date || e.start_date)) : [];
  const onExport = exportPdf;
  const [weekStart, setWeekStart] = useState(() => { const s = sessionStorage.getItem("avail-week"); const t = s ? new Date(s) : romeNow(); return s ? t : addDays(t, -(((t.getDay() + 6) % 7))); });
  useEffect(() => { sessionStorage.setItem("avail-week", weekStart.toISOString()); }, [weekStart]);
  const [hlId, setHlId] = useState(() => sessionStorage.getItem("avail-highlight"));
  useEffect(() => {
    if (!hlId) return undefined;
    sessionStorage.removeItem("avail-highlight");
    const t1 = setTimeout(() => { document.querySelector(".avail-blink")?.scrollIntoView({ block: "center", behavior: "smooth" }); }, 300);
    const t2 = setTimeout(() => setHlId(null), 4500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [hlId]);
  const hlEv = hlId ? (events || []).find((e) => e.id === hlId) : null;
  const hlDay = (dk) => !!hlEv && hlEv.start_date <= dk && dk <= (hlEv.end_date || hlEv.start_date);
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const DAY_LABELS = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];
  const baseTechs = (technicians || []).filter((t) => t.role !== "collaborator");
  const [orderIds, setOrderIds] = useState(null);
  const ordered = orderIds ? orderIds.map((id) => baseTechs.find((t) => t.id === id)).filter(Boolean).concat(baseTechs.filter((t) => !orderIds.includes(t.id))) : baseTechs;
  const techs = [...ordered.filter((t) => !t.videomaker && !t.warehouse && !t.subcontractor), ...ordered.filter((t) => t.subcontractor && !t.videomaker && !t.warehouse), ...ordered.filter((t) => t.warehouse && !t.videomaker), ...ordered.filter((t) => t.videomaker)];
  const firstSub = techs.findIndex((t) => t.subcontractor && !t.videomaker && !t.warehouse);
  const firstVm = techs.findIndex((t) => t.videomaker);
  const firstWh = techs.findIndex((t) => t.warehouse && !t.videomaker);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }), useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 6 } }));
  const [swapAsk, setSwapAsk] = useState(null);
  const reqById = useMemo(() => Object.fromEntries((requests || []).map((r) => [r.id, r])), [requests]);
  const SWAP_OK = (s1, s2) => (s1 === "in_attesa" && s2 === "in_attesa") || (["disponibile", "confermato"].includes(s1) && ["disponibile", "confermato"].includes(s2));
  const collision = (args) => {
    const isReq = String(args.active.id).startsWith("req:");
    const containers = args.droppableContainers.filter((c) => /^(cell|drop):/.test(String(c.id)) === isReq);
    if (!isReq) return closestCenter({ ...args, droppableContainers: containers });
    const hits = pointerWithin({ ...args, droppableContainers: containers });
    return hits.sort((x, y) => (String(y.id).startsWith("drop:") ? 1 : 0) - (String(x.id).startsWith("drop:") ? 1 : 0));
  };
  const onDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const aid = String(active.id);
    if (aid.startsWith("req:")) {
      const a = reqById[aid.slice(4)];
      const oid = String(over.id);
      if (oid.startsWith("drop:")) {
        const b = reqById[oid.slice(5)];
        if (!b || b.id === a.id || b.technician_id === a.technician_id) return;
        if (b.date !== a.date) { toast.error("Lo scambio si fa solo nello stesso giorno"); return; }
        if (!SWAP_OK(a.status, b.status)) { toast.error("Scambio non permesso tra questi stati"); return; }
        setSwapAsk({ a, b });
      } else {
        const [, techId, date] = oid.split(":");
        if (techId === a.technician_id) return;
        if (date !== a.date) { toast.error("Puoi spostarla solo nello stesso giorno"); return; }
        if ((requests || []).some((r) => r.technician_id === techId && r.date === date)) { toast.error("Trascinala sopra una scritta per scambiare"); return; }
        if (unavail.some((u) => u.tech_id === techId && u.start <= date && date <= u.end)) { toast.error("Questo tecnico non è disponibile in quella data"); return; }
        setSwapAsk({ a, tech: techs.find((t) => t.id === techId) });
      }
      return;
    }
    const ids = techs.map((t) => t.id);
    const next = arrayMove(ids, ids.indexOf(active.id), ids.indexOf(over.id));
    setOrderIds(next);
    try { await onReorder(next); } catch (err) { toast.error("Ordine non salvato"); setOrderIds(null); }
  };
  const doSwap = async () => {
    const { a, b, tech } = swapAsk;
    setSwapAsk(null);
    try { await onSwap(a.id, b ? { target_request_id: b.id } : { target_technician_id: tech.id }); toast.success(b ? "Scambio fatto" : "Spostato"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const todayK = dayKey(romeNow());
  const ORDER = { disponibile: 0, forse: 1, in_attesa: 2, non_disponibile: 3 };
  const TXT = { disponibile: "text-emerald-300", forse: "text-purple-300", in_attesa: "text-amber-300", non_disponibile: "text-red-400" };
  const pos = useMemo(() => {
    const reqN = Object.fromEntries((events || []).map((e) => [e.id, e.required_technicians || 0]));
    const reqV = Object.fromEntries((events || []).map((e) => [e.id, e.required_videomakers || 0]));
    const g = {};
    (requests || []).forEach((r) => { if (r.status === "confermato" && !r.supervisor) { const k = `${r.event_id}|${r.date}|${r.videomaker && reqV[r.event_id] ? 1 : r.warehouse ? 2 : 0}`; (g[k] = g[k] || []).push(r); } });
    const m = {};
    Object.values(g).forEach((rs) => {
      rs.sort((a, b) => (a.confirmed_at || a.responded_at || "").localeCompare(b.confirmed_at || b.responded_at || "") || (a.technician_name || "").localeCompare(b.technician_name || ""));
      const tot = Math.max(rs[0].videomaker && reqV[rs[0].event_id] ? reqV[rs[0].event_id] : rs[0].warehouse ? 0 : reqN[rs[0].event_id] || 0, rs.length);
      rs.forEach((r, i) => { m[r.id] = `${i + 1}/${tot}`; });
    });
    return m;
  }, [requests, events]);
  const allestEv = useMemo(() => new Set((events || []).filter((e) => /allestiment/i.test(e.category || "")).map((e) => e.id)), [events]);
  const { evColor, evLight } = useMemo(() => {
    const PAL = ["#2563eb", "#ea580c", "#c026d3", "#0d9488", "#ca8a04", "#be123c", "#65a30d", "#7c2d12", "#6d28d9", "#0891b2", "#475569"];
    const sorted = [...(events || [])].sort((a, b) => (a.start_date || "").localeCompare(b.start_date || "") || String(a.id).localeCompare(String(b.id)));
    // every event (allestimenti included) gets its own colour: fixed palette first, then generated hues
    const out = {}, light = {};
    sorted.forEach((e, n) => {
      out[e.id] = n < PAL.length ? PAL[n] : `hsl(${(n * 137.5) % 360}, ${55 + (n % 3) * 10}%, ${32 + (n % 4) * 5}%)`;
    });
    return { evColor: out, evLight: light };
  }, [events]);
  const cellFor = (techId, day) => {
    const di = dayKey(day);
    const rs = (requests || []).filter((r) => r.technician_id === techId && (r.date || "") <= di && di <= (r.date_end || r.date || ""));
    const nm = (r) => r.event_id ? (r.event_name || r.title || "Servizio") : r.status === "disponibile" ? "Disponibile" : r.status === "in_attesa" ? "Rich" : (r.title || "Disponibilità?");
    const conf = rs.find((r) => r.status === "confermato");
    const hl = !!hlId && rs.some((r) => r.event_id === hlId);
    if (rs.some((r) => r.slot_start)) {
      const all = rs.filter((r) => r.status === "confermato" || r.status in ORDER)
        .sort((a, b) => (a.slot_start || "00:00").localeCompare(b.slot_start || "00:00"));
      return { hl, partitioned: true, cls: "", hasReq: true, isUnav: false, reqId: null, count: 0,
        items: all.map((r) => ({ id: r.id, status: r.status, generic: !r.event_id, draft: r.draft, note: r.response_message, sup: r.supervisor,
          text: `${r.slot_start ? `${r.slot_start}–${r.slot_end} ` : ""}${nm(r)}${r.status === "confermato" && pos[r.id] && !r.supervisor ? ` ${pos[r.id]}` : ""}`,
          cls: r.status === "confermato" ? (evLight[r.event_id] ? "text-slate-900" : "text-white") : TXT[r.status],
          bg: r.status === "confermato" ? evColor[r.event_id] || "#2563eb" : undefined })) };
    }
    if (conf) return { hl, items: [{ id: conf.id, status: conf.status, text: `${nm(conf)}${pos[conf.id] && !conf.supervisor ? ` ${pos[conf.id]}` : ""}`, sup: conf.supervisor, cls: "" }], cls: evLight[conf.event_id] ? "text-slate-900" : "text-white", bg: evColor[conf.event_id] || "#2563eb" };
    const sorted = rs.filter((r) => r.status in ORDER).sort((a, b) => ORDER[a.status] - ORDER[b.status]);
    const pend = sorted.find((r) => r.status === "forse" || r.status === "in_attesa");
    const unavRec = unavail.find((u) => u.tech_id === techId && u.start <= di && di <= u.end);
    const isUnav = !!unavRec;
    const unavItem = isUnav ? [{ id: `u-${unavRec.id}-${di}`, uid: unavRec.id, date: di, status: "unavail", text: "Non disponibile", cls: "text-red-400" }] : [];
    return { hl, items: [...sorted.map((r) => ({ id: r.id, status: r.status, text: nm(r), sup: r.supervisor, generic: !r.event_id, cls: TXT[r.status], draft: r.draft, note: r.response_message })), ...unavItem],
      cls: isUnav && !sorted.length ? "bg-red-500/15" : "", hasReq: sorted.length > 0, isUnav,
      reqId: sorted.some((r) => r.status === "disponibile") || pend?.draft ? null : pend?.id, count: pend?.reminder_count || 0 };
  };
  return (
    <div className="space-y-4" data-testid="availability-board">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" data-testid="avail-prev" className="h-8 w-8 border-slate-700" onClick={() => setWeekStart((w) => addDays(w, -7))}><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" size="sm" data-testid="avail-today" className="h-8 border-slate-700" onClick={() => { const t = romeNow(); setWeekStart(addDays(t, -(((t.getDay() + 6) % 7)))); }}>Oggi</Button>
          <Button variant="outline" size="icon" data-testid="avail-next" className="h-8 w-8 border-slate-700" onClick={() => setWeekStart((w) => addDays(w, 7))}><ChevronRight className="w-4 h-4" /></Button>
          <span className="ml-2 text-sm font-semibold text-slate-200" data-testid="avail-week-label">{days[0].toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" })} – {days[6].toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" })}</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
        {onMarkUnavailMany && (
          <Button variant="outline" data-testid="unav-select-toggle" onClick={() => (selMode ? exitSel() : setSelMode(true))}
            className={selMode ? "border-red-500 bg-red-500/15 text-red-200 hover:bg-red-500/25" : "border-red-500/40 text-red-300 hover:bg-red-500/10"}>
            <XCircle className="w-4 h-4 mr-1.5" /> {selMode ? "Esci dalla selezione" : "Segna non disponibili"}
          </Button>
        )}
        {onUndo && (
          <Button variant="outline" data-testid="avail-undo-btn" disabled={!undoCount || undoing}
            onClick={async () => { setUndoing(true); try { await onUndo(); } finally { setUndoing(false); } }}
            className="border-slate-700 text-slate-200 hover:bg-slate-800">
            {undoing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Undo2 className="w-4 h-4 mr-1.5" />} Annulla{undoCount ? ` (${undoCount})` : ""}
          </Button>
        )}
        {onFlush && (
          <Button data-testid="flush-notifications-btn" disabled={!pendingCount || flushing}
            onClick={async () => { setFlushing(true); try { await onFlush(); } finally { setFlushing(false); } }}
            className={pendingCount ? "bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold" : "bg-slate-800 text-slate-500"}>
            {flushing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />} Invia notifiche{pendingCount ? ` (${pendingCount})` : ""}
          </Button>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button data-testid="export-pdf-btn" className="bg-cyan-500 hover:bg-cyan-600 text-slate-950 font-semibold"><Download className="w-4 h-4 mr-1.5" /> Esporta PDF</Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-slate-900 border-slate-700 text-slate-100">
            <DropdownMenuItem data-testid="export-week" onClick={() => onExport("week", dayKey(weekStart))} className="cursor-pointer focus:bg-slate-800 focus:text-white"><CalendarDays className="w-4 h-4 mr-2 text-cyan-400" /> Questa settimana</DropdownMenuItem>
            <DropdownMenuItem data-testid="export-month" onClick={() => onExport("month", dayKey(days[3]))} className="cursor-pointer focus:bg-slate-800 focus:text-white"><CalendarDays className="w-4 h-4 mr-2 text-cyan-400" /> Questo mese</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        {sheetExport && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="export-csv-btn" className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-semibold"><Download className="w-4 h-4 mr-1.5" /> File per Fogli Google</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-slate-900 border-slate-700 text-slate-100">
              <DropdownMenuItem data-testid="export-csv-week" onClick={() => onExport("week", dayKey(weekStart), "csv")} className="cursor-pointer focus:bg-slate-800 focus:text-white"><CalendarDays className="w-4 h-4 mr-2 text-emerald-400" /> Questa settimana</DropdownMenuItem>
              <DropdownMenuItem data-testid="export-csv-month" onClick={() => onExport("month", dayKey(days[3]), "csv")} className="cursor-pointer focus:bg-slate-800 focus:text-white"><CalendarDays className="w-4 h-4 mr-2 text-emerald-400" /> Questo mese</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        </div>
      </div>
      {selMode && (
        <div data-testid="unav-select-bar" className="sticky top-2 z-20 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-red-500/40 bg-slate-900/95 backdrop-blur px-3 py-2">
          <span className="text-sm text-red-200" data-testid="unav-select-count">{selN ? `${selN} giorn${selN === 1 ? "o" : "i"} selezionat${selN === 1 ? "o" : "i"}` : "Tocca le celle dei tecnici per selezionare i giorni"}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" data-testid="unav-select-cancel" onClick={exitSel} className="border-slate-700">Annulla</Button>
            <Button size="sm" data-testid="unav-select-save" disabled={!selN || selBusy} onClick={saveSel} className="bg-red-500 hover:bg-red-600 text-white">
              {selBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <XCircle className="w-4 h-4 mr-1.5" />} Segna non disponibile
            </Button>
          </div>
        </div>
      )}
      <DndContext sensors={sensors} collisionDetection={collision} onDragEnd={onDragEnd}>
      <SortableContext items={techs.map((t) => t.id)} strategy={verticalListSortingStrategy}>
      <div className="overflow-auto rounded-xl border border-slate-800 max-h-[calc(100dvh-380px)] min-h-[260px] sm:max-h-[calc(100dvh-250px)]" data-testid="availability-scroll">
        <table className="w-full border-collapse text-sm" data-testid="availability-table">
          <thead className="sticky top-0 z-20">
            <tr className="bg-slate-950">
              <th className="sticky left-0 z-30 bg-slate-950 text-left px-3 py-2 font-semibold text-slate-200 border-b border-slate-800 min-w-[140px]">Tecnico</th>
              {days.map((d, i) => (
                <th key={i} className={`bg-slate-950 px-2 py-2 text-center font-semibold border-b border-l border-slate-800 min-w-[110px] ${dayKey(d) === todayK ? "text-cyan-400" : "text-slate-300"} ${hlDay(dayKey(d)) ? "avail-blink-head" : ""}`} data-testid={`avail-head-${dayKey(d)}`}>
                  <div className="text-xs">{DAY_LABELS[i]}</div>
                  <div className="text-[11px] text-slate-500">{d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" })}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {techs.length === 0 ? (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-500">Nessun tecnico presente.</td></tr>
            ) : techs.map((t, ti) => [
              ti === firstSub && (
                <tr key="sub-sep" data-testid="avail-subcontractor-separator">
                  <td colSpan={8} className="sticky left-0 bg-teal-500/10 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-teal-300 border-y border-teal-500/30">Subappaltatori</td>
                </tr>
              ),
              ti === firstWh && (
                <tr key="wh-sep" data-testid="avail-warehouse-separator">
                  <td colSpan={8} className="sticky left-0 bg-amber-500/10 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-amber-300 border-y border-amber-500/30">Magazzinieri</td>
                </tr>
              ),
              ti === firstVm && (
                <tr key="vm-sep" data-testid="avail-videomaker-separator">
                  <td colSpan={8} className="sticky left-0 bg-pink-500/10 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-pink-300 border-y border-pink-500/30">Videomaker</td>
                </tr>
              ),
              <SortableRow key={t.id} id={t.id} enabled={!!onReorder} name={t.name}>
                {days.map((d, i) => { const c = cellFor(t.id, d); const dk = dayKey(d); const selectable = selMode && !c.isUnav && !c.items.some((x) => x.status === "confermato"); const picked = !!sel[`${t.id}|${dk}`]; return (
                  <DropCell key={i} id={`cell:${t.id}:${dk}`} enabled={!!onSwap && !selMode}
                    onClick={selectable ? () => toggleSel(t.id, dk) : onCellComment ? () => onCellComment(t, dk, c.items.map((x) => x.text).join(" / ")) : undefined}
                    testId={selMode ? `unav-sel-cell-${t.id}-${dk}` : onCellComment ? `collab-cell-${t.id}-${dk}` : undefined}
                    style={c.bg && !picked ? { backgroundColor: c.bg } : undefined}
                    className={`px-2 py-2 text-center border-b border-l border-slate-800 text-xs font-bold ${c.cls} ${selectable ? "cursor-pointer hover:bg-red-500/10" : ""} ${picked ? "!bg-red-500/40 ring-2 ring-inset ring-red-400" : ""} ${c.hl ? "avail-blink" : ""}`}>
                    {selMode ? (
                      <div className="pointer-events-none flex flex-col items-center gap-0.5 min-w-0">
                        {picked ? <span className="text-red-100">Non disponibile</span> : c.items.length ? c.items.map((it) => <span key={it.id} className={`break-words ${it.cls}`}>{it.text}</span>) : <span className="text-slate-700 font-normal">—</span>}
                      </div>
                    ) : (
                    <div className="flex items-center justify-center gap-1">
                      <div className="flex flex-col items-center gap-0.5 min-w-0">
                        {c.items.map((it) => (
                          <DragItem key={it.id} it={it} allest={allestEv.has(reqById[it.id]?.event_id)} enabled={!!onSwap && ["disponibile", "confermato", "in_attesa"].includes(it.status)}
                            onClick={it.status === "unavail" ? (onMarkUnavail ? () => setUnavAsk({ ...it, techName: t.name }) : undefined) : onRemoveRequest ? () => setConfirmAsk(reqById[it.id]) : undefined} />
                        ))}
                        {!c.hasReq && !c.isUnav && !c.items.some((x) => x.status === "confermato") && (onCellEvent ? (
                          <button data-testid={`avail-add-${t.id}-${dayKey(d)}`} title="Nuovo evento per questo tecnico"
                            onClick={() => setCellPick({ tech: t, date: dayKey(d), unav: c.isUnav })}
                            className="text-slate-600 hover:text-cyan-300 hover:bg-cyan-500/10 rounded p-0.5 transition-colors"><Plus className="w-3.5 h-3.5" /></button>
                        ) : !c.items.length && <span className="text-slate-700 font-normal">—</span>)}
                        {onCellEvent && c.items.some((x) => x.status !== "unavail") && (
                          <button data-testid={`avail-partition-${t.id}-${dayKey(d)}`} title="Aggiungi partizione: un altro servizio in questo giorno con orario"
                            onClick={(ev) => { ev.stopPropagation(); openPartition(t, dayKey(d)); }}
                            className="text-slate-500 hover:text-cyan-300 hover:bg-cyan-500/10 rounded p-0.5 transition-colors"><SplitSquareHorizontal className="w-3.5 h-3.5" /></button>
                        )}
                      </div>
                      {c.reqId && onRemind && (
                        <div className="flex flex-col items-center shrink-0 leading-none">
                          <button data-testid={`avail-remind-${c.reqId}`} onClick={() => onRemind(c.reqId)} title="Sollecita"
                            className="text-amber-300 hover:text-amber-200">
                            <Send className="w-3.5 h-3.5" />
                          </button>
                          <span data-testid={`avail-remind-count-${c.reqId}`} className="mt-0.5 text-[10px] text-slate-400">{c.count}×</span>
                        </div>
                      )}
                    </div>
                    )}
                  </DropCell>
                ); })}
              </SortableRow>,
            ])}
          </tbody>
          {techs.length > 0 && (
            <tfoot data-testid="avail-free-row">
              {[["Tecnici liberi", techs.filter((t) => !t.videomaker && !t.warehouse && !t.subcontractor), "tech"], ["Subappaltatori liberi", techs.filter((t) => t.subcontractor && !t.videomaker && !t.warehouse), "sub"], ["Magazzinieri liberi", techs.filter((t) => t.warehouse && !t.videomaker), "wh"], ["Videomaker liberi", techs.filter((t) => t.videomaker), "vm"]].filter(([, list]) => list.length).map(([label, list, k]) => (
              <tr key={k} className="bg-slate-900/80">
                <td className="sticky left-0 z-10 bg-slate-900 px-3 py-2 text-xs font-semibold text-slate-300 border-t border-slate-700">{label}</td>
                {days.map((d, i) => { const dk = dayKey(d); const n = list.filter((t) => !cellFor(t.id, d).isUnav && !(requests || []).some((r) => r.technician_id === t.id && r.date === dk && !["in_attesa", "forse"].includes(r.status))).length; return (
                  <td key={i} data-testid={`avail-free-${k}-${dayKey(d)}`} className="px-2 py-2 text-center border-t border-l border-slate-700">
                    <span className={`inline-block rounded-md px-2 py-0.5 text-xs font-bold ${n ? "bg-lime-400/10 text-lime-300" : "bg-rose-500/10 text-rose-300"}`}>{n}/{list.length}</span>
                  </td>
                ); })}
              </tr>
              ))}
            </tfoot>
          )}
        </table>
      </div>
      </SortableContext>
      </DndContext>
      <AlertDialog open={!!confirmAsk} onOpenChange={(o) => { if (!o) { setConfirmAsk(null); setDanger(null); } }}>
        <AlertDialogContent data-testid="avail-action-dialog" className="bg-slate-900 border-slate-800 text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmAsk?.technician_name} · {confirmAsk?.event_name || "Disponibilità senza evento"}</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              {confirmAsk ? `${formatRange(confirmAsk.date, confirmAsk.date)} · ${STATUS[confirmAsk.status]?.label || confirmAsk.status}. Le notifiche partiranno quando premi "Invia notifiche".` : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {confirmAsk?.response_message && (
            <p data-testid="avail-action-note" className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-2.5 text-sm text-amber-200"><StickyNote className="inline w-4 h-4 mr-1 -mt-0.5" />Nota del tecnico: {confirmAsk.response_message}</p>
          )}
          {danger ? (
            <div data-testid="avail-danger-confirm" className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">
              {danger === "remove" ? `Togliere ${confirmAsk?.technician_name} da "${confirmAsk?.event_name}" in questo giorno?` : `Eliminare l'evento "${confirmAsk?.event_name}" per tutti i tecnici e tutti i giorni?`}
              <div className="flex gap-2 mt-3">
                <Button variant="outline" size="sm" data-testid="avail-danger-cancel" onClick={() => setDanger(null)} className="border-slate-700 text-slate-300">No</Button>
                <Button size="sm" data-testid="avail-danger-yes" className="bg-red-600 hover:bg-red-700 text-white"
                  onClick={async () => { const r = confirmAsk; const d = danger; setConfirmAsk(null); setDanger(null); if (d === "remove") await onRemoveRequest(r); else await onDeleteEvent(r); }}>
                  Sì, {danger === "remove" ? "togli" : "elimina"}</Button>
              </div>
            </div>
          ) : (
            <div className="grid gap-2">
              {confirmAsk && !confirmAsk.event_id && onMoveEvent && (
                <div className="space-y-1.5" data-testid="avail-assign-generic">
                  <Label className="text-xs text-slate-400">Assegna a un evento di quel giorno (resta {(STATUS[confirmAsk.status]?.label || "").toLowerCase()}, nessun avviso)</Label>
                  {moveTargets.length ? moveTargets.map((e) => (
                    <Button key={e.id} variant="outline" data-testid={`avail-assign-generic-${e.id}`} onClick={async () => { const r = confirmAsk; setConfirmAsk(null); await onMoveEvent(r, e); }}
                      className="w-full justify-start border-cyan-500/40 text-cyan-200 hover:bg-cyan-500/10"><ArrowRightLeft className="w-4 h-4 mr-1.5" /> {e.name}</Button>
                  )) : <p className="text-xs text-slate-500">Nessun evento in questa data.</p>}
                </div>
              )}
              {confirmAsk && confirmAsk.event_id && onMoveEvent && (
                <div className="space-y-1" data-testid="avail-move-event">
                  <Label className="text-xs text-slate-400">Sposta su un altro evento dello stesso giorno (stesso stato, nessun avviso)</Label>
                  {moveTargets.length ? (
                    <Select onValueChange={async (v) => { const r = confirmAsk; const ev = moveTargets.find((e) => e.id === v); setConfirmAsk(null); await onMoveEvent(r, ev); }}>
                      <SelectTrigger data-testid="avail-move-event-select" className="bg-slate-800 border-slate-700"><SelectValue placeholder="Scegli evento…" /></SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                        {moveTargets.map((e) => <SelectItem key={e.id} value={e.id} data-testid={`avail-move-opt-${e.id}`}>{e.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : <p className="text-xs text-slate-500">Nessun altro evento in questa data.</p>}
                </div>
              )}
              {confirmAsk?.status === "non_disponibile" && onToUnavail && (
                <Button variant="outline" data-testid="avail-to-unavail-btn" onClick={async () => { const r = confirmAsk; setConfirmAsk(null); await onToUnavail(r); }}
                  className="border-red-500/50 text-red-300 hover:bg-red-500/10"><XCircle className="w-4 h-4 mr-1.5" /> Trasforma in Non disponibile (tutta la giornata)</Button>
              )}
              {["disponibile", "in_attesa"].includes(confirmAsk?.status) && confirmAsk?.event_id && onDetach && (
                <Button variant="outline" data-testid="avail-detach-btn" onClick={async () => { const r = confirmAsk; setConfirmAsk(null); await onDetach(r); }}
                  className="border-lime-400/40 text-lime-300 hover:bg-lime-400/10"><HelpCircle className="w-4 h-4 mr-1.5" /> {confirmAsk.status === "in_attesa" ? "Passa a richiesta senza evento" : "Passa a disponibilità generica"}</Button>
              )}
              {confirmAsk?.status === "disponibile" && confirmAsk?.event_id && onConfirm && (
                <Button data-testid="avail-confirm-btn" onClick={async () => { const r = confirmAsk; setConfirmAsk(null); await onConfirm(r.id); }}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold"><ShieldCheck className="w-4 h-4 mr-1.5" /> Conferma</Button>
              )}
              {confirmAsk?.status === "confermato" && onToggleSupervisor && (
                <Button variant="outline" data-testid="avail-toggle-supervisor-btn" onClick={async () => { const r = confirmAsk; setConfirmAsk(null); await onToggleSupervisor(r); }}
                  className="border-violet-500/40 text-violet-300 hover:bg-violet-500/10"><ShieldCheck className="w-4 h-4 mr-1.5" /> {confirmAsk.supervisor ? "Togli supervisore" : "Rendi supervisore"}</Button>
              )}
              {confirmAsk && confirmAsk.status !== "non_disponibile" && onSetSlot && (
                <SlotEditor key={confirmAsk.id} r={confirmAsk} onSave={async (s, e) => { const r = confirmAsk; setConfirmAsk(null); await onSetSlot(r, s, e); }} />
              )}
              {confirmAsk && onCellEvent && confirmAsk.status !== "non_disponibile" && (
                <Button variant="outline" data-testid="avail-partition-btn"
                  onClick={() => { const r = confirmAsk; setConfirmAsk(null); const t = (technicians || []).find((x) => x.id === r.technician_id); if (t) openPartition(t, r.date); }}
                  className="border-cyan-500/40 text-cyan-200 hover:bg-cyan-500/10"><SplitSquareHorizontal className="w-4 h-4 mr-1.5" /> Aggiungi partizione al giorno (altro servizio con orario)</Button>
              )}
              {onRemoveRequest && <Button variant="outline" data-testid="avail-remove-btn" onClick={() => setDanger("remove")} className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"><X className="w-4 h-4 mr-1.5" /> Togli da questa persona</Button>}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="avail-confirm-cancel" className="bg-slate-800 border-slate-700 text-slate-200">Chiudi</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!unavAsk} onOpenChange={(o) => { if (!o) setUnavAsk(null); }}>
        <AlertDialogContent data-testid="unav-remove-dialog" className="bg-slate-900 border-slate-800 text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle>Togliere "Non disponibile"?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              {unavAsk ? `${unavAsk.techName} tornerà convocabile il ${formatRange(unavAsk.date, unavAsk.date)}. Gli altri giorni del periodo restano non disponibili.` : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="unav-remove-cancel" className="bg-slate-800 border-slate-700 text-slate-200">Annulla</AlertDialogCancel>
            <Button data-testid="unav-remove-confirm" onClick={async () => { const u = unavAsk; setUnavAsk(null); await onUnmarkUnavail(u.uid, u.date); }}
              className="bg-red-600 hover:bg-red-700 text-white font-semibold">Togli</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!swapAsk} onOpenChange={(o) => { if (!o) setSwapAsk(null); }}>
        <AlertDialogContent data-testid="swap-confirm-dialog" className="bg-slate-900 border-slate-800 text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle>{swapAsk?.b ? "Scambiare i servizi?" : "Spostare la richiesta?"}</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              {swapAsk?.b
                ? `${swapAsk.a.technician_name} passa su "${swapAsk.b.event_name}" e ${swapAsk.b.technician_name} su "${swapAsk.a.event_name}". Ognuno mantiene il proprio stato. Nessun avviso verrà inviato.`
                : swapAsk ? `"${swapAsk.a.event_name}" passa da ${swapAsk.a.technician_name} a ${swapAsk.tech?.name} con lo stesso stato. Entrambi verranno avvisati quando premi "Invia notifiche".` : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="swap-cancel-btn" className="bg-slate-800 border-slate-700 text-slate-200">Annulla</AlertDialogCancel>
            <Button data-testid="swap-confirm-btn" onClick={doSwap} className="bg-cyan-500 hover:bg-cyan-600 text-slate-950 font-semibold">Conferma</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {onCellEvent && (
        <Dialog open={!!cellPick} onOpenChange={(o) => { if (!o) setCellPick(null); }}>
          <DialogContent data-testid="cell-pick-dialog" className="bg-slate-900 border-slate-800 text-slate-100 max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display">{cellPick?.partition ? "Nuova partizione · " : ""}{cellPick?.tech.name} · {cellPick ? formatRange(cellPick.date, cellPick.date) : ""}</DialogTitle>
              <DialogDescription className="text-slate-400 text-sm">
                {cellPick?.partition ? "Aggiungi un altro servizio in questo giorno indicando l'orario. Non deve sovrapporsi agli altri servizi con orario del tecnico. Se il tecnico è già confermato in questo giorno, la partizione risulta subito confermata (nessuna richiesta di disponibilità). " : ""}
                {cellPick?.tech.employee ? "Verrà messo come Disponibile solo per questo giorno (dipendente, nessuna mail)." : "Riceverà la richiesta di disponibilità solo per questo giorno, quando premi Invia notifiche."}
                {cellPick?.unav && <span data-testid="cell-pick-unav-warning" className="mt-2 flex items-center gap-1.5 text-red-400 font-semibold"><AlertTriangle className="w-4 h-4" /> Segnato come non disponibile in questa data</span>}
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-2" data-testid="cell-pick-role">
              <button type="button" data-testid="cell-pick-role-tech" onClick={() => setSupMode(false)}
                className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${!supMode ? "border-cyan-500 bg-cyan-500/10 text-cyan-300" : "border-slate-700 text-slate-400"}`}>Tecnico</button>
              <button type="button" data-testid="cell-pick-role-sup" onClick={() => setSupMode(true)}
                className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${supMode ? "border-violet-500 bg-violet-500/10 text-violet-300" : "border-slate-700 text-slate-400"}`}>Supervisore</button>
            </div>
            {supMode && <p className="text-xs text-slate-500 -mt-1">Il supervisore non conta nei tecnici previsti e non cambia i mancanti.</p>}
            {cellPick?.partition && (
              <div className="grid grid-cols-2 gap-2" data-testid="partition-times">
                <div className="space-y-1"><Label className="text-xs text-slate-400">Dalle</Label>
                  <Input type="time" data-testid="partition-start" value={slot.start} onChange={(e) => setSlot((s) => ({ ...s, start: e.target.value }))} className="bg-slate-800 border-slate-700" /></div>
                <div className="space-y-1"><Label className="text-xs text-slate-400">Alle</Label>
                  <Input type="time" data-testid="partition-end" value={slot.end} onChange={(e) => setSlot((s) => ({ ...s, end: e.target.value }))} className="bg-slate-800 border-slate-700" /></div>
                {!slotOk && <p className="col-span-2 text-xs text-amber-300" data-testid="partition-hint">Inserisci l'orario (l'inizio deve essere prima della fine) per scegliere l'evento.</p>}
              </div>
            )}
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Aggiungi a evento già programmato</p>
              {dayEvents.length === 0 ? (
                <p className="text-sm text-slate-500" data-testid="cell-pick-no-events">Nessun evento in questa data.</p>
              ) : dayEvents.map((e) => (
                <button key={e.id} data-testid={`cell-pick-event-${e.id}`} disabled={cellPick?.partition && !slotOk}
                  onClick={async () => { const c = cellPick; setCellPick(null); await onCellEvent(c.tech, e, [c.date], supMode, false, c.partition ? { ...slot } : null); setSupMode(false); }}
                  className="w-full text-left rounded-lg border border-slate-700 hover:border-cyan-500/60 hover:bg-cyan-500/5 px-3 py-2 transition-colors disabled:opacity-40 disabled:pointer-events-none">
                  <div className="text-sm font-semibold">{e.name}</div>
                  <div className="text-xs text-slate-500">{formatRange(e.start_date, e.end_date)}{e.location ? ` · ${e.location}` : ""}</div>
                </button>
              ))}
            </div>
            <DialogFooter className="flex-col gap-2 sm:flex-col">
              <Button data-testid="cell-pick-new-btn" disabled={cellPick?.partition && !slotOk} onClick={() => { const c = cellPick; setCellPick(null); setCellNew({ ...c, sup: supMode, slot: c.partition ? { ...slot } : null }); setSupMode(false); }}
                className="w-full bg-cyan-500 hover:bg-cyan-600 text-slate-950 font-semibold"><Plus className="w-4 h-4 mr-1.5" /> Crea nuovo evento</Button>
              {onAskGeneric && !cellPick?.tech.employee && !cellPick?.partition && (
                <Button variant="outline" data-testid="cell-pick-generic-btn" onClick={async () => { const c = cellPick; setCellPick(null); await onAskGeneric(c.tech, c.date); }}
                  className="w-full border-lime-400/40 text-lime-300 hover:bg-lime-400/10"><HelpCircle className="w-4 h-4 mr-1.5" /> Chiedi solo disponibilità (senza evento)</Button>
              )}
              {onMarkUnavail && !cellPick?.unav && !cellPick?.partition && (
                <Button variant="outline" data-testid="cell-pick-unav-btn" onClick={async () => { const c = cellPick; setCellPick(null); await onMarkUnavail(c.tech, c.date); }}
                  className="w-full border-red-500/40 text-red-300 hover:bg-red-500/10"><XCircle className="w-4 h-4 mr-1.5" /> Segna non disponibile questo giorno</Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {onCellEvent && (
        <NewEventDialog open={!!cellNew} onOpenChange={(o) => { if (!o) setCellNew(null); }}
          defaultDate={cellNew ? { start: cellNew.date, end: cellNew.date } : undefined}
          forTech={cellNew?.tech}
          onDone={async (ev) => { const c = cellNew; setCellNew(null); if (ev && c) await onCellEvent(c.tech, ev, undefined, !!c.sup, true, c.slot || null); }} />
      )}
      {onReorder && <p className="text-xs text-slate-500" data-testid="reorder-hint">Le modifiche fatte qui non inviano notifiche finché non premi "Invia notifiche". Tocca una scritta per confermare, spostare o togliere la persona. Trascina un tecnico dalla maniglia a sinistra del nome per cambiarne la posizione (su telefono tieni premuto).</p>}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400">
        <span className="font-semibold text-slate-300">Il nome evento indica dove è stato interpellato; il colore lo stato:</span>
        <span className="rounded px-1.5 py-0.5 text-white font-bold" style={{ background: "linear-gradient(90deg,#2563eb,#0d9488,#c026d3,#ea580c)" }}>Confermato (colore dell'evento)</span>
        <span className="text-emerald-300 font-bold">Disponibile</span>
        <span className="text-amber-300 font-bold">In attesa</span>
        <span className="text-purple-300 font-bold">Forse</span>
        <span className="text-red-400 font-bold">Non disponibile</span>
        <span className="text-slate-300 inline-flex items-center gap-1" data-testid="legend-allest"><Hammer className="w-3.5 h-3.5" /> Allestimento</span>
      </div>
    </div>
  );
}

export function EventsCalendar({ events, requests, onOpen, onOpenWeek, onAddOnDate, action }) {
  const clickTimer = useRef(null);
  const clickEvent = (e) => {
    clearTimeout(clickTimer.current);
    if (!onOpenWeek) { onOpen(e); return; }
    clickTimer.current = setTimeout(() => onOpen(e), 250);
  };
  const dblClickEvent = (day, e) => { clearTimeout(clickTimer.current); onOpenWeek && onOpenWeek(day, e); };
  const [cursor, setCursor] = useState(() => { const s = sessionStorage.getItem("events-cal-month"); return s ? new Date(s) : romeNow(); });
  useEffect(() => { sessionStorage.setItem("events-cal-month", cursor.toISOString()); }, [cursor]);
  const year = cursor.getFullYear(), month = cursor.getMonth();
  const { byName } = useCategories();
  const todayKey = dayKey(romeNow());

  const first = new Date(year, month, 1);
  const startOffset = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const totalCells = Math.ceil((startOffset + daysInMonth) / 7) * 7;
  const gridStart = new Date(year, month, 1 - startOffset);
  const weeks = [];
  for (let i = 0; i < totalCells; i += 7) {
    weeks.push(Array.from({ length: 7 }, (_, j) => addDays(gridStart, i + j)));
  }

  const statusOrder = { confermato: 0, disponibile: 1, forse: 2, in_attesa: 3, non_disponibile: 4 };
  const dotsFor = (e, ds) => {
    const all = (requests || []).filter((r) => r.event_id === e.id);
    const sorted = all.filter((r) => r.date === ds && !r.supervisor && !(r.videomaker && e.required_videomakers) && !r.warehouse).map((r) => r.status).sort((a, b) => (statusOrder[a] ?? 9) - (statusOrder[b] ?? 9));
    const n = e.required_technicians != null ? e.required_technicians : techCount(all);
    if (n === 0) {
      // no technicians required: show videomakers / subcontractors slots
      const others = all.filter((r) => r.date === ds && !r.supervisor && !r.warehouse).map((r) => r.status).sort((a, b) => (statusOrder[a] ?? 9) - (statusOrder[b] ?? 9));
      const m = Math.max(e.required_videomakers || 0, others.filter((s) => s !== "non_disponibile").length);
      return Array.from({ length: Math.min(m, 20) }, (_, idx) => others[idx] || null);
    }
    const techDots = Array.from({ length: Math.min(n, 20) }, (_, idx) => sorted[idx] || null);
    const nv = e.required_videomakers || 0;
    if (!nv) return techDots;
    // videomakers: one dot per required videomaker, same colours as technicians
    const vm = all.filter((r) => r.date === ds && !r.supervisor && r.videomaker).map((r) => r.status).sort((a, b) => (statusOrder[a] ?? 9) - (statusOrder[b] ?? 9));
    const out = [...techDots, ...Array.from({ length: Math.min(nv, 20) }, (_, idx) => vm[idx] || null)];
    out.vmStart = techDots.length;
    return out;
  };

  const gapFor = (ds) => (events || []).reduce((sum, e) => {
    if (e.required_technicians == null || !e.start_date || ds < e.start_date || ds > (e.end_date || e.start_date)) return sum;
    const conf = (requests || []).filter((r) => r.event_id === e.id && r.date === ds && r.status === "confermato" && !r.supervisor && !(r.videomaker && e.required_videomakers) && !r.warehouse).length;
    return sum + Math.max(0, e.required_technicians - conf);
  }, 0);

  const [dragStart, setDragStart] = useState(null);
  const [dragEnd, setDragEnd] = useState(null);
  const finalizeDrag = () => {
    if (!dragStart) return;
    const earlier = dragStart.getTime() <= (dragEnd || dragStart).getTime();
    const s = earlier ? dragStart : (dragEnd || dragStart);
    const e = earlier ? (dragEnd || dragStart) : dragStart;
    setDragStart(null); setDragEnd(null);
    if (onAddOnDate) onAddOnDate(dayKey(s), dayKey(e));
  };
  const cancelDrag = () => { setDragStart(null); setDragEnd(null); };

  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900 p-4 sm:p-5 select-none" data-testid="events-calendar" onMouseUp={finalizeDrag} onMouseLeave={cancelDrag}>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h3 className="font-display text-lg font-semibold">{MONTHS[month]} {year}</h3>
        <div className="flex items-center gap-2 flex-wrap">
          {action}
          <Button variant="outline" size="icon" className="h-8 w-8 border-slate-700" onClick={() => setCursor(new Date(year, month - 1, 1))}><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" size="sm" className="h-8 border-slate-700" onClick={() => setCursor(romeNow())}>Oggi</Button>
          <Button variant="outline" size="icon" className="h-8 w-8 border-slate-700" onClick={() => setCursor(new Date(year, month + 1, 1))}><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1.5 mb-1.5">
        {WEEKDAYS.map((w) => <div key={w} className="text-center text-xs font-mono uppercase tracking-wider text-slate-500 py-1">{w}</div>)}
      </div>
      <div className="space-y-1.5">
        {weeks.map((week, wi) => {
          const weekStart = week[0], weekEnd = week[6];
          const segs = (events || []).map((e) => {
            if (!e.start_date) return null;
            const s = parseDay(e.start_date);
            const en = parseDay(e.end_date || e.start_date);
            if (en < weekStart || s > weekEnd) return null;
            const startCol = Math.max(0, diffDays(weekStart, s));
            const endCol = Math.min(6, diffDays(weekStart, en));
            return { e, startCol, endCol, span: endCol - startCol + 1, isStart: diffDays(weekStart, s) >= 0, isEnd: diffDays(weekStart, en) <= 6 };
          }).filter(Boolean).sort((a, b) => a.startCol - b.startCol || b.span - a.span);

          const lanes = [];
          segs.forEach((seg) => {
            let lane = 0;
            for (;;) {
              lanes[lane] = lanes[lane] || new Array(7).fill(false);
              let free = true;
              for (let c = seg.startCol; c <= seg.endCol; c++) if (lanes[lane][c]) { free = false; break; }
              if (free) { for (let c = seg.startCol; c <= seg.endCol; c++) lanes[lane][c] = true; seg.lane = lane; break; }
              lane++;
            }
          });
          const laneCount = lanes.length;
          const visibleSegs = segs;
          const weekMinH = Math.max(96, 34 + laneCount * 36 + 6);

          return (
            <div key={wi} className="relative grid grid-cols-7 gap-1.5" style={{ minHeight: weekMinH }}>
              {week.map((d, di) => {
                const inMonth = d.getMonth() === month;
                const isToday = dayKey(d) === todayKey;
                const ds = dayKey(d);
                const gap = inMonth && ds >= todayKey ? gapFor(ds) : 0;
                const waiting = inMonth ? (requests || []).filter((r) => r.date === ds && r.status === "in_attesa").length : 0;
                return (
                  <div key={di}
                    data-testid={`cal-day-${ds}`}
                    onMouseDown={() => { if (inMonth && onAddOnDate) { setDragStart(d); setDragEnd(d); } }}
                    onMouseEnter={() => { if (inMonth && dragStart) setDragEnd(d); }}
                    title={inMonth && onAddOnDate ? "Clicca per 1 giorno, oppure tieni premuto e trascina fino alla data di fine" : undefined}
                    style={{ minHeight: weekMinH }}
                    className={`relative rounded-lg border p-1.5 transition-colors ${inMonth ? (gap > 0 ? "cursor-pointer hover:border-rose-400 bg-rose-500/10 border-rose-500/60" : "cursor-pointer hover:border-cyan-500/60 bg-slate-900/40 border-slate-800/60") : "bg-slate-900/10 border-slate-900/40"} ${isToday ? "ring-1 ring-cyan-500" : ""} ${dragStart && d.getTime() >= Math.min(dragStart.getTime(), (dragEnd || dragStart).getTime()) && d.getTime() <= Math.max(dragStart.getTime(), (dragEnd || dragStart).getTime()) ? "!bg-cyan-500/20 !border-cyan-400" : ""}`}>
                    <span className={`text-xs font-medium ${!inMonth ? "text-slate-600" : isToday ? "text-cyan-400" : "text-slate-400"}`}>{d.getDate()}</span>
                    {waiting > 0 && (
                      <>
                        <span data-testid={`cal-waiting-${ds}`} title={`${waiting} richiest${waiting === 1 ? "a" : "e"} ancora senza risposta`}
                          className="ml-1 hidden sm:inline-flex items-center gap-0.5 rounded-full bg-amber-400/90 px-1 text-[9px] font-bold text-slate-950 align-middle">
                          <Clock className="w-2.5 h-2.5" />{waiting}
                        </span>
                        <span data-testid={`cal-waiting-dot-${ds}`} className={`sm:hidden absolute ${gap > 0 ? "top-5" : "top-1.5"} right-1 w-2 h-2 rounded-full bg-amber-400 ring-2 ring-slate-950`} />
                      </>
                    )}
                    {gap > 0 && (
                      <span data-testid={`cal-gap-${ds}`} title={`Mancano ${gap} tecnici confermati`} className="absolute top-1 right-1.5 rounded bg-rose-500/90 px-1 text-[9px] font-bold text-white">-{gap}</span>
                    )}
                  </div>
                );
              })}
              <div className="pointer-events-none absolute inset-0 pt-7 space-y-1">
                {Array.from({ length: laneCount }).map((_, laneIdx) => (
                  <div key={laneIdx} className="grid grid-cols-7 gap-1.5">
                    {visibleSegs.filter((seg) => seg.lane === laneIdx).map((seg) => {
                      const e = seg.e;
                      const hasCat = e.category && byName[e.category];
                      const radius = `${seg.isStart ? "rounded-l" : "rounded-l-none"} ${seg.isEnd ? "rounded-r" : "rounded-r-none"}`;
                      const barStyle = { gridColumn: `${seg.startCol + 1} / span ${seg.span}` };
                      if (hasCat) Object.assign(barStyle, catChipSolid(byName[e.category].color, byName[e.category].text_color));
                      return (
                        <button key={e.id} onClick={(ev) => { ev.stopPropagation(); clickEvent(e); }}
                          onDoubleClick={(ev) => { ev.stopPropagation(); dblClickEvent(addDays(weekStart, seg.startCol), e); }}
                          data-testid={`ecal-event-${e.id}`} title={e.name}
                          style={barStyle}
                          className={`pointer-events-auto h-[32px] overflow-hidden flex flex-col justify-center gap-0.5 text-left text-[10px] font-bold px-1.5 border ${radius} ${hasCat ? "" : "bg-cyan-500/20 text-cyan-200 border-cyan-500/40"}`}>
                          <span className={`truncate leading-[13px] ${seg.isStart ? "" : "opacity-80"}`}>{e.hide_name && <EyeOff data-testid={`cal-hidden-name-${e.id}`} className="inline w-3 h-3 mr-0.5 -mt-0.5" aria-label="Nome nascosto ai freelance" />}{e.name}</span>
                          <span className="grid w-full gap-1.5" style={{ gridTemplateColumns: `repeat(${seg.span}, minmax(0, 1fr))` }}>
                            {Array.from({ length: seg.span }).map((_, k) => {
                              const ds = dayKey(addDays(weekStart, seg.startCol + k));
                              return (
                                <span key={k} className="flex items-center gap-0.5 overflow-hidden h-2" data-testid={`ecal-dots-${e.id}-${ds}`}>
                                  {((dl) => dl.map((st, idx) => (
                                    <span key={idx} title={st || "slot libero"} data-vm-start={idx === dl.vmStart && idx > 0 ? "1" : undefined}
                                      className={`w-1.5 h-1.5 shrink-0 rounded-full ${idx === dl.vmStart && idx > 0 ? "ml-1.5" : ""} ${st ? statusDot[st] : "bg-black/20 border border-white/40"}`} />
                                  )))(dotsFor(e, ds))}
                                </span>
                              );
                            })}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-400" data-testid="calendar-legend">
        <span className="flex items-center gap-2"><span className="inline-flex items-center gap-0.5 rounded-full bg-amber-400/90 px-1 text-[9px] font-bold text-slate-950"><Clock className="w-2.5 h-2.5" />N</span> Richieste ancora senza risposta</span>
        <span className="flex items-center gap-2"><EyeOff className="w-3.5 h-3.5 text-slate-300" /> Nome nascosto ai freelance (vedono "Evento generico")</span>
        <span className="flex items-center gap-2"><span className="inline-block w-5 h-3 rounded-sm border border-rose-500/60 bg-rose-500/10 shrink-0" /> Giorno con tecnici confermati mancanti (-N)</span>
      </div>
    </div>
  );
}

/* ----------------------------- Calendar helpers -------------------------- */
const WEEKDAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

function dayKey(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function parseDay(s) { return new Date(s + "T00:00:00"); }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function diffDays(a, b) { return Math.round((b - a) / 86400000); }

/* ----------------------------- New event -------------------------------- */
const UNDO_SID = Math.random().toString(36).slice(2, 10);

function PeopleTable({ list, empty, onOpen, onResend }) {
  if (!list.length) return <EmptyState text={empty} />;
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-800">
      <table className="w-full text-sm">
        <thead className="bg-slate-900 text-slate-400 text-xs uppercase tracking-wider">
          <tr>
            <th className="text-left font-medium px-4 py-3">Nome</th>
            <th className="text-left font-medium px-4 py-3 hidden sm:table-cell">Email</th>
            <th className="text-left font-medium px-4 py-3 hidden sm:table-cell">Telefono</th>
            <th className="text-left font-medium px-4 py-3 hidden md:table-cell">Compleanno</th>
            <th className="text-left font-medium px-4 py-3">Skills</th>
            <th className="text-right font-medium px-4 py-3">Azioni</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800">
          {list.map((t) => (
            <tr key={t.id} data-testid={`technician-card-${t.id}`} onClick={() => onOpen(t)}
              className="bg-slate-900/60 hover:bg-slate-800/60 cursor-pointer transition-colors">
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${t.videomaker ? "bg-pink-500/15 text-pink-300" : t.warehouse ? "bg-amber-500/15 text-amber-300" : t.subcontractor ? "bg-teal-500/15 text-teal-300" : "bg-cyan-500/15 text-cyan-300"}`}>{t.name?.charAt(0)}</div>
                  <span className="font-medium">{t.name}</span>
                  {t.subcontractor ? <SubcontractorBadge /> : t.employee && <EmployeeBadge />}
                  {t.warehouse && <WarehouseBadge />}
                  {t.videomaker && <VideomakerBadge />}
                  {t.linked_manager && <span data-testid="linked-manager-badge" className="text-[10px] font-bold rounded px-1.5 py-0.5 border border-cyan-500/40 bg-cyan-500/10 text-cyan-300">TU</span>}
                  {!t.subcontractor && !t.linked_manager && t.report_count !== undefined && (
                    <span data-testid={`tech-report-count-${t.id}`} title="Report di fine evento compilati" className="inline-flex items-center gap-1 whitespace-nowrap shrink-0 text-[10px] font-bold rounded px-1.5 py-0.5 border border-amber-500/40 bg-amber-500/10 text-amber-300">
                      <ClipboardList className="w-3 h-3" />{t.report_count} report</span>
                  )}
                </div>
              </td>
              <td className="px-4 py-3 hidden sm:table-cell text-slate-400">{t.linked_manager ? "Account manager · nessuna notifica" : t.email}</td>
              <td className="px-4 py-3 hidden sm:table-cell text-slate-400">{t.phone || "—"}</td>
              <td className="px-4 py-3 hidden md:table-cell text-slate-400">{t.birthday ? formatDate(t.birthday) : "—"}</td>
              <td className="px-4 py-3"><div className="flex flex-wrap gap-1">{t.skills?.map((s) => <SkillBadge key={s} id={s} />)}</div></td>
              <td className="px-4 py-3 text-right">
                {!t.linked_manager && !t.subcontractor && (
                  <Button size="sm" variant="outline" data-testid={`resend-password-btn-${t.id}`}
                    onClick={(e) => { e.stopPropagation(); onResend(t); }}
                    className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10 h-8">
                    <KeyRound className="w-4 h-4 mr-1.5" /> Reinvia password
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NewTechnicianDialog({ onDone, videomaker = false, warehouse = false, subcontractor = false }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", birthday: "", skills: [], role: "technician", employee: false, videomaker, warehouse, subcontractor });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (id) => setForm((f) => ({ ...f, skills: f.skills.includes(id) ? f.skills.filter((x) => x !== id) : [...f.skills, id] }));
  const submit = async () => {
    if (!form.name || !form.email) { toast.error("Nome ed email sono obbligatori"); return; }
    setLoading(true);
    try {
      const res = await api.post("/technicians", form);
      const pw = res.data.temp_password;
      const sent = res.data.email_sent;
      const label = form.role === "collaborator" ? "Collaboratore" : form.subcontractor ? "Subappaltatore" : form.videomaker ? "Videomaker" : form.warehouse ? "Magazziniere" : "Tecnico";
      if (form.subcontractor) {
        toast.success(`${label} creato (nessun accesso all'app, nessuna email inviata).`);
      } else if (sent) {
        toast.success(`${label} creato. Email con le credenziali inviata a ${form.email}.${pw ? ` Password: ${pw}` : ""}`, { duration: 14000 });
      } else {
        toast.warning(`${label} creato ma l'email non è partita. Comunica tu le credenziali — Password: ${pw}`, { duration: 20000 });
      }
      setForm({ name: "", email: "", phone: "", birthday: "", skills: [], role: "technician", employee: false, videomaker, warehouse, subcontractor });
      setOpen(false);
      onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={subcontractor ? "new-subcontractor-btn" : videomaker ? "new-videomaker-btn" : warehouse ? "new-warehouse-btn" : "new-technician-btn"} className={subcontractor ? "bg-teal-500 hover:bg-teal-600 text-slate-950 font-semibold" : videomaker ? "bg-pink-500 hover:bg-pink-600 text-white font-semibold" : warehouse ? "bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold" : "bg-cyan-500 hover:bg-cyan-600 text-slate-950 font-semibold"}>
          <UserPlus className="w-4 h-4 mr-1.5" /> {subcontractor ? "Nuovo Subappaltatore" : videomaker ? "Nuovo Videomaker" : warehouse ? "Nuovo Magazziniere" : "Nuovo Tecnico"}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">{form.role === "collaborator" ? "Nuovo Collaboratore" : form.subcontractor ? "Nuovo Subappaltatore" : form.videomaker ? "Nuovo Videomaker" : form.warehouse ? "Nuovo Magazziniere" : "Nuovo Tecnico"}</DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">{form.subcontractor ? "Non avrà l'app: l'email serve solo per scrivergli tu dopo la conferma." : "Aggiungi un utente. Riceverà una password provvisoria da comunicargli."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {!form.subcontractor && <div className="space-y-1.5">
            <Label>Tipo di utente</Label>
            <div className="flex gap-2">
              <button type="button" data-testid="role-technician" onClick={() => setForm((f) => ({ ...f, role: "technician" }))}
                className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${form.role !== "collaborator" ? "border-cyan-500 bg-cyan-500/10 text-cyan-300" : "border-slate-700 text-slate-400"}`}>Tecnico</button>
              <button type="button" data-testid="role-collaborator" onClick={() => setForm((f) => ({ ...f, role: "collaborator" }))}
                className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${form.role === "collaborator" ? "border-cyan-500 bg-cyan-500/10 text-cyan-300" : "border-slate-700 text-slate-400"}`}>Collaboratore (sola lettura)</button>
            </div>
          </div>}
          <div className="space-y-1.5"><Label>Nome</Label><Input data-testid="tech-name-input" value={form.name} onChange={set("name")} placeholder="Mario Rossi" className="bg-slate-800 border-slate-700" /></div>
          <div className="space-y-1.5"><Label>Email</Label><Input type="email" data-testid="tech-email-input" value={form.email} onChange={set("email")} placeholder="mario@email.com" className="bg-slate-800 border-slate-700" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Telefono</Label><Input data-testid="tech-phone-input" value={form.phone} onChange={set("phone")} placeholder="+39 ..." className="bg-slate-800 border-slate-700" /></div>
            <div className="space-y-1.5"><Label>Compleanno</Label><Input type="date" data-testid="tech-birthday-input" value={form.birthday} onChange={set("birthday")} className="bg-slate-800 border-slate-700" /></div>
          </div>
          {form.role !== "collaborator" && !form.subcontractor && (
            <>
            <EmployeeToggle value={form.employee} onChange={(v) => setForm((f) => ({ ...f, employee: v }))} testid="tech-employee-toggle" />
            <VideomakerToggle value={form.videomaker} onChange={(v) => setForm((f) => ({ ...f, videomaker: v }))} testid="tech-videomaker-toggle" />
            <WarehouseToggle value={form.warehouse} onChange={(v) => setForm((f) => ({ ...f, warehouse: v }))} testid="tech-warehouse-toggle" />
            </>
          )}
          {form.role !== "collaborator" && (
          <div className="space-y-1.5">
            <Label>Skills</Label>
            <div className="flex flex-wrap gap-2">
              {SKILLS.map((s) => {
                const a = form.skills.includes(s.id);
                return (
                  <button key={s.id} type="button" data-testid={`tech-skill-${s.id}`} onClick={() => toggle(s.id)}
                    className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-all ${a ? s.color : "border-slate-700 text-slate-400"}`}>
                    <s.icon className="w-3.5 h-3.5" /> {s.label}
                  </button>
                );
              })}
            </div>
          </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={loading} data-testid="save-technician-btn" className="bg-cyan-500 hover:bg-cyan-600 text-slate-950 font-semibold">{form.role === "collaborator" ? "Crea collaboratore" : form.subcontractor ? "Crea subappaltatore" : form.videomaker ? "Crea videomaker" : form.warehouse ? "Crea magazziniere" : "Crea tecnico"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EmployeeToggle({ value, onChange, testid }) {
  return (
    <div className="space-y-1.5">
      <Label>Contratto</Label>
      <div className="grid grid-cols-2 gap-2">
        <button type="button" data-testid={`${testid}-freelance`} onClick={() => onChange(false)}
          className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${!value ? "border-cyan-500 bg-cyan-500/10 text-cyan-300" : "border-slate-700 text-slate-400"}`}>Freelance</button>
        <button type="button" data-testid={`${testid}-employee`} onClick={() => onChange(true)}
          className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${value ? "border-amber-500 bg-amber-500/10 text-amber-300" : "border-slate-700 text-slate-400"}`}>Dipendente</button>
      </div>
      {value && <p className="text-xs text-slate-500">Non riceve richieste di disponibilità: lo assegni tu agli eventi.</p>}
    </div>
  );
}

function VideomakerToggle({ value, onChange, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={() => onChange(!value)}
      className={`w-full flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${value ? "border-pink-500 bg-pink-500/10 text-pink-300" : "border-slate-700 text-slate-400"}`}>
      <span className={`w-4 h-4 rounded border flex items-center justify-center ${value ? "bg-pink-500 border-pink-500" : "border-slate-600"}`}>{value && <Check className="w-3 h-3 text-slate-950" />}</span>
      Videomaker <span className="text-xs font-normal text-slate-500">(gruppo a parte, non conta nei tecnici)</span>
    </button>
  );
}

export const SubcontractorBadge = () => (
  <span data-testid="subcontractor-badge" className="text-[10px] font-bold tracking-wide rounded px-1.5 py-0.5 border border-teal-500/40 bg-teal-500/10 text-teal-300">SUBAPPALTO</span>
);

function SubcontractorToggle({ value, onChange, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={() => onChange(!value)}
      className={`w-full flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${value ? "border-teal-500 bg-teal-500/10 text-teal-300" : "border-slate-700 text-slate-400"}`}>
      <span className={`w-4 h-4 rounded border flex items-center justify-center ${value ? "bg-teal-500 border-teal-500" : "border-slate-600"}`}>{value && <Check className="w-3 h-3 text-slate-950" />}</span>
      Subappaltatore <span className="text-xs font-normal text-slate-500">(senza app, conta come tecnico)</span>
    </button>
  );
}

export const WarehouseBadge = () => (
  <span data-testid="warehouse-badge" className="text-[10px] font-bold tracking-wide rounded px-1.5 py-0.5 border border-amber-500/40 bg-amber-500/10 text-amber-300">MAGAZZINO</span>
);

function WarehouseToggle({ value, onChange, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={() => onChange(!value)}
      className={`w-full flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${value ? "border-amber-500 bg-amber-500/10 text-amber-300" : "border-slate-700 text-slate-400"}`}>
      <span className={`w-4 h-4 rounded border flex items-center justify-center ${value ? "bg-amber-500 border-amber-500" : "border-slate-600"}`}>{value && <Check className="w-3 h-3 text-slate-950" />}</span>
      Magazziniere <span className="text-xs font-normal text-slate-500">(gruppo a parte, non conta nei tecnici)</span>
    </button>
  );
}

export const VideomakerBadge = () => (
  <span data-testid="videomaker-badge" className="text-[10px] font-bold tracking-wide rounded px-1.5 py-0.5 border border-pink-500/40 bg-pink-500/10 text-pink-300">VIDEOMAKER</span>
);

export const EmployeeBadge = () => (
  <span className="text-[10px] font-bold tracking-wide rounded px-1.5 py-0.5 border border-amber-500/40 bg-amber-500/10 text-amber-300">DIPENDENTE</span>
);

function HideNameToggle({ value, onChange, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={() => onChange(!value)}
      className={`mt-1 w-full flex items-center gap-2 rounded-lg border px-3 py-2 text-xs text-left transition-colors ${value ? "border-amber-500/60 bg-amber-500/10 text-amber-200" : "border-slate-700 text-slate-400 hover:bg-slate-800"}`}>
      <span className={`w-8 h-4 rounded-full relative shrink-0 transition-colors ${value ? "bg-amber-500" : "bg-slate-700"}`}>
        <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-transform ${value ? "translate-x-4" : "translate-x-0.5"}`} />
      </span>
      <span>Nascondi il nome ai freelance <span className="text-slate-500">(vedranno "Evento generico", con la categoria)</span></span>
    </button>
  );
}

function QuickNames({ onPick }) {
  const [names, setNames] = useState([]);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  useEffect(() => { api.get("/settings/quick-names").then((r) => setNames(r.data.names)).catch(() => {}); }, []);
  const save = async (next) => {
    try { const r = await api.put("/settings/quick-names", { names: next }); setNames(r.data.names); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const add = () => { if (draft.trim()) { save([...names, draft.trim()]); setDraft(""); } };
  const chip = "rounded-full border px-2.5 py-0.5 text-xs transition-colors";
  return (
    <div className="pt-1 space-y-1.5" data-testid="quick-names">
      <div className="flex flex-wrap items-center gap-1.5">
        {names.map((q) => editing ? (
          <span key={q} className={`${chip} border-slate-600 text-slate-200 flex items-center gap-1`}>
            {q}
            <button type="button" data-testid={`quick-name-remove-${q.toLowerCase()}`} onClick={() => save(names.filter((n) => n !== q))} className="text-slate-400 hover:text-red-400"><X className="w-3 h-3" /></button>
          </span>
        ) : (
          <button key={q} type="button" data-testid={`quick-name-${q.toLowerCase()}`} onClick={() => onPick(q)}
            className={`${chip} border-slate-700 text-slate-300 hover:border-cyan-500/60 hover:text-cyan-200`}>{q}</button>
        ))}
        <button type="button" data-testid="quick-names-edit-btn" onClick={() => setEditing((e) => !e)}
          className={`${chip} border-dashed border-slate-600 text-slate-400 hover:text-cyan-200 flex items-center gap-1`}>
          {editing ? <><Check className="w-3 h-3" /> Fatto</> : <><Pencil className="w-3 h-3" /> Modifica</>}
        </button>
      </div>
      {editing && (
        <div className="flex gap-2">
          <Input data-testid="quick-name-new-input" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Nuovo nome rapido"
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} className="bg-slate-800 border-slate-700 h-8 text-sm" />
          <Button type="button" size="sm" data-testid="quick-name-add-btn" onClick={add} className="bg-cyan-500 hover:bg-cyan-600 text-slate-950 h-8"><Plus className="w-3.5 h-3.5" /></Button>
        </div>
      )}
    </div>
  );
}

function VideomakerCountSelect({ value, onChange, testid, required = false }) {
  return (
    <div className="space-y-1.5">
      <Label>Videomaker richiesti {required && <span className="text-red-400">*</span>}</Label>
      <Select value={String(value || 0)} onValueChange={onChange}>
        <SelectTrigger data-testid={testid} className="bg-slate-800 border-slate-700"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-slate-900 border-slate-700 text-slate-100 max-h-60">
          <SelectItem value="0">Nessuno</SelectItem>
          {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function NewEventDialog({ onDone, open: openProp, onOpenChange, defaultDate, forTech }) {
  const { categories } = useCategories();
  const controlled = openProp !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlled ? openProp : internalOpen;
  const setOpen = controlled ? onOpenChange : setInternalOpen;
  const [form, setForm] = useState({ name: "", client_name: "", start_date: "", end_date: "", start_time: "", end_time: "", location: "", notes: "", category: "none", required_technicians: "none", hide_name: true });
  const fictitious = false;
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (open && defaultDate) {
      if (typeof defaultDate === "string") setForm((f) => ({ ...f, start_date: defaultDate }));
      else setForm((f) => ({ ...f, start_date: defaultDate.start || "", end_date: defaultDate.end || defaultDate.start || "" }));
    }
    // eslint-disable-next-line
  }, [open, defaultDate]);

  const submit = async () => {
    if (!form.name || !form.start_date) {
      toast.error("Nome e data di inizio sono obbligatori");
      return;
    }
    const rental = /noleggi/i.test(form.category || "");
    const vmCat = /videomaker/i.test(form.category || "");
    if (vmCat && !Number(form.required_videomakers || 0)) {
      toast.error("Indica quanti videomaker servono");
      return;
    }
    if (!rental && !vmCat && form.required_technicians === "none" && !Number(form.required_videomakers || 0)) {
      toast.error("Indica almeno un tecnico o un videomaker");
      return;
    }
    if (!fictitious && form.category === "none") {
      toast.error("La categoria è obbligatoria");
      return;
    }
    if (form.end_date && form.end_date < form.start_date) {
      toast.error("La data di fine non può precedere quella di inizio");
      return;
    }
    setLoading(true);
    try {
      const created = await api.post("/events", { ...form, start_time: form.start_time || null, end_time: form.end_time || null, is_fictitious: fictitious, category: fictitious ? null : (form.category === "none" ? null : form.category), required_technicians: rental || vmCat || form.required_technicians === "none" ? null : Number(form.required_technicians), required_videomakers: rental ? 0 : Number(form.required_videomakers || 0) });
      toast.success("Evento creato");
      setForm({ name: "", client_name: "", start_date: "", end_date: "", start_time: "", end_time: "", location: "", notes: "", category: "none", required_technicians: "none", hide_name: true });
      setOpen(false);
      onDone(created.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!controlled && (
        <DialogTrigger asChild>
          <Button data-testid="new-event-btn" className="bg-cyan-500 hover:bg-cyan-600 text-slate-950 font-semibold">
            <Plus className="w-4 h-4 mr-1.5" /> Nuovo Evento
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
        <DialogHeader><DialogTitle className="font-display">Nuovo Evento</DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">{forTech ? (forTech.employee ? `${forTech.name} verrà messo come Disponibile (dipendente, nessuna mail).` : `A ${forTech.name} arriverà la richiesta di disponibilità.`) : "Crea un evento, anche su più giorni."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Nome evento</Label>
            <Input data-testid="event-name-input" value={form.name} onChange={set("name")}
              placeholder="Festival Summer 2026" className="bg-slate-800 border-slate-700" />
            <QuickNames onPick={(q) => setForm((f) => ({ ...f, name: f.name && !f.name.startsWith(q) ? `${q} ${f.name}` : q }))} />
            <HideNameToggle value={form.hide_name} onChange={(v) => setForm((f) => ({ ...f, hide_name: v }))} testid="event-hide-name" />
          </div>
          {!fictitious && (
          <div className="space-y-1.5">
            <Label>Categoria <span className="text-red-400">*</span></Label>
            <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
              <SelectTrigger data-testid="event-category-select" className="bg-slate-800 border-slate-700"><SelectValue placeholder="Categoria" /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                <SelectItem value="none" disabled>Seleziona…</SelectItem>
                {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          )}
          {/noleggi/i.test(form.category || "") ? (
            <p data-testid="rental-no-techs-note" className="rounded-lg border border-slate-700 bg-slate-800/50 px-3 py-2 text-xs text-slate-400">Noleggio: non servono tecnici né videomaker.</p>
          ) : /videomaker/i.test(form.category || "") ? (
            <VideomakerCountSelect required value={form.required_videomakers} onChange={(v) => setForm((f) => ({ ...f, required_videomakers: v }))} testid="event-videomakers-select" />
          ) : (<>
          <div className="space-y-1.5">
            <Label>Tecnici richiesti <span className="text-red-400">*</span> <span className="text-[11px] font-normal text-slate-500">(almeno un tecnico o un videomaker)</span></Label>
            <Select value={form.required_technicians} onValueChange={(v) => setForm((f) => ({ ...f, required_technicians: v }))}>
              <SelectTrigger data-testid="event-required-select" className="bg-slate-800 border-slate-700"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-slate-100 max-h-60">
                <SelectItem value="none">Nessuno</SelectItem>
                {Array.from({ length: 20 }, (_, i) => i + 1).map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <VideomakerCountSelect value={form.required_videomakers} onChange={(v) => setForm((f) => ({ ...f, required_videomakers: v }))} testid="event-videomakers-select" />
          </>)}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Dal</Label>
              <Input type="date" data-testid="event-start-date-input" value={form.start_date} onChange={set("start_date")}
                className="bg-slate-800 border-slate-700" />
            </div>
            <div className="space-y-1.5">
              <Label>Al (opzionale)</Label>
              <Input type="date" data-testid="event-end-date-input" value={form.end_date} onChange={set("end_date")}
                min={form.start_date || undefined} className="bg-slate-800 border-slate-700" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Luogo</Label>
            <Input data-testid="event-location-input" value={form.location} onChange={set("location")}
              placeholder="Piazza..." className="bg-slate-800 border-slate-700" />
          </div>
          <p className="text-xs text-slate-500">Orari, note logistica e allegati si aggiungono dopo, aprendo l'evento → Modifica / Allegati.</p>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={loading} data-testid="save-event-btn"
            className="bg-cyan-500 hover:bg-cyan-600 text-slate-950 font-semibold">Crea evento</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* --------------------------- Technician picker -------------------------- */
function TechnicianPicker({ technicians, skill, selected, setSelected, conflicts = {}, unavailable = {} }) {
  const list = useMemo(
    () => (skill === "all" ? technicians : technicians.filter((t) => t.skills?.includes(skill))),
    [technicians, skill]
  );
  const toggle = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  return (
    <div className="rounded-lg border border-slate-700 bg-slate-800/40 max-h-52 overflow-y-auto divide-y divide-slate-800" data-testid="technician-picker">
      {list.length === 0 && <p className="text-sm text-slate-500 p-3">Nessun tecnico con questa skill.</p>}
      {list.map((t) => {
        const active = selected.includes(t.id);
        return (
          <button key={t.id} type="button" data-testid={`pick-tech-${t.id}`} onClick={() => toggle(t.id)}
            className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-slate-800 transition-colors">
            <div className="flex items-center gap-2 min-w-0">
              <div className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 ${active ? "bg-cyan-500 border-cyan-500" : "border-slate-600"}`}>
                {active && <Check className="w-3.5 h-3.5 text-slate-950" />}
              </div>
              <div className="min-w-0">
                <span className="truncate text-sm block">{t.name}{t.videomaker && <span className="ml-1.5 text-[10px] font-bold text-pink-300">VIDEOMAKER</span>}</span>
                {(unavailable[t.id] || []).map((u) => (
                  <span key={u.id} data-testid={`pick-unavail-${t.id}-${u.start}`} className="flex items-center gap-1 text-[11px] font-semibold text-red-400">
                    <XCircle className="w-3 h-3 shrink-0" /> Non disponibile {u.start === u.end ? `il ${formatRange(u.start, u.start)}` : `dal ${formatRange(u.start, u.start)} al ${formatRange(u.end, u.end)}`}
                  </span>
                ))}
                {(conflicts[t.id] || []).map((o) => (
                  <span key={o.id} data-testid={`pick-conflict-${t.id}-${o.date}`} className="flex items-center gap-1 text-[11px] text-amber-300">
                    <AlertTriangle className="w-3 h-3 shrink-0" /> Già {(STATUS[o.status]?.label || o.status).toLowerCase()} il {formatRange(o.date, o.date)} su {o.event_name || "altro evento"} — non verrà richiesto
                  </span>
                ))}
              </div>
            </div>
            <div className="flex gap-1 shrink-0">{t.skills?.slice(0, 2).map((s) => <SkillBadge key={s} id={s} />)}</div>
          </button>
        );
      })}
    </div>
  );
}

/* ------------------------ Move request select --------------------------- */
function MoveSelect({ request, events, onMoved, onNew }) {
  const others = (events || []).filter((e) => e.id !== request.event_id && e.start_date <= request.date && request.date <= (e.end_date || e.start_date));
  const move = async (eventId) => {
    if (eventId === "__new__") { onNew(request); return; }
    try {
      await api.put(`/requests/${request.id}/move`, { event_id: eventId });
      toast.success("Tecnico spostato nell'evento selezionato");
      onMoved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <div className="flex items-center gap-2">
      <ArrowRightLeft className="w-3.5 h-3.5 text-slate-500 shrink-0" />
      <Select value="" onValueChange={move}>
        <SelectTrigger data-testid={`move-select-${request.id}`} className="h-8 bg-slate-800 border-slate-700 text-xs">
          <SelectValue placeholder="Sposta (stessa data)…" />
        </SelectTrigger>
        <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
          {others.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} — {formatRange(e.start_date, e.end_date)}</SelectItem>)}
          <SelectItem value="__new__" data-testid={`move-new-event-${request.id}`}>+ Nuovo evento in questa data…</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

/* -------------------------- Technician detail --------------------------- */
function UnavailSection({ techId, items, onChanged }) {
  const [f, setF] = useState({ start: "", end: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!f.start) { toast.error("Indica la data di inizio"); return; }
    setBusy(true);
    try { await api.post("/unavailability", { tech_id: techId, start: f.start, end: f.end || f.start, reason: f.reason }); setF({ start: "", end: "", reason: "" }); toast.success("Periodo aggiunto"); onChanged(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const del = async (id) => {
    try { await api.delete(`/unavailability/${id}`); onChanged(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 space-y-2" data-testid="unavail-section">
      <h4 className="text-sm font-semibold text-red-300 flex items-center gap-1.5"><XCircle className="w-4 h-4" /> Periodi non disponibile</h4>
      {items.length === 0 ? <p className="text-xs text-slate-500">Nessun periodo inserito.</p> : items.map((u) => (
        <div key={u.id} data-testid={`unavail-item-${u.id}`} className="flex items-center justify-between gap-2 text-sm">
          <span>{formatRange(u.start, u.end)}{u.reason ? <span className="text-slate-500"> · {u.reason}</span> : null}</span>
          <button data-testid={`unavail-delete-${u.id}`} onClick={() => del(u.id)} className="text-slate-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      <div className="grid grid-cols-2 gap-2 pt-1">
        <Input type="date" data-testid="unavail-start-input" value={f.start} onChange={(e) => setF((x) => ({ ...x, start: e.target.value }))} className="bg-slate-800 border-slate-700 h-9" />
        <Input type="date" data-testid="unavail-end-input" value={f.end} min={f.start} onChange={(e) => setF((x) => ({ ...x, end: e.target.value }))} className="bg-slate-800 border-slate-700 h-9" />
      </div>
      <div className="flex gap-2">
        <Input data-testid="unavail-reason-input" value={f.reason} onChange={(e) => setF((x) => ({ ...x, reason: e.target.value }))} placeholder="Motivo (facoltativo)" className="bg-slate-800 border-slate-700 h-9" />
        <Button data-testid="unavail-add-btn" onClick={add} disabled={busy} className="bg-red-500 hover:bg-red-600 text-white h-9 shrink-0"><Plus className="w-4 h-4 mr-1" /> Aggiungi</Button>
      </div>
    </div>
  );
}

function TechnicianDetailDialog({ technician, requests, unavail = [], onUnavailChanged, onClose, onDeleted }) {
  const [tech, setTech] = useState(technician);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", birthday: "", skills: [], employee: false });
  const [saving, setSaving] = useState(false);
  const groups = [
    { key: "confermato", title: "Impegni confermati" },
    { key: "disponibile", title: "Disponibilità date" },
    { key: "forse", title: "Forse" },
    { key: "non_disponibile", title: "Indisponibilità date" },
    { key: "in_attesa", title: "In attesa di risposta" },
  ];
  const startEdit = () => {
    setForm({
      name: tech.name || "", email: tech.email || "", phone: tech.phone || "",
      birthday: tech.birthday || "", skills: tech.skills || [], employee: !!tech.employee, videomaker: !!tech.videomaker, warehouse: !!tech.warehouse, subcontractor: !!tech.subcontractor,
    });
    setEdit(true);
  };
  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleSkill = (id) => setForm((f) => ({ ...f, skills: f.skills.includes(id) ? f.skills.filter((x) => x !== id) : [...f.skills, id] }));
  const saveEdit = async () => {
    if (!form.name || !form.email) { toast.error("Nome ed email sono obbligatori"); return; }
    setSaving(true);
    try {
      const res = await api.put(`/technicians/${tech.id}`, {
        name: form.name, email: form.email, phone: form.phone,
        birthday: form.birthday || null, skills: form.skills, employee: form.employee, videomaker: !!form.videomaker, warehouse: !!form.warehouse, subcontractor: !!form.subcontractor,
      });
      setTech(res.data);
      toast.success("Profilo tecnico aggiornato");
      setEdit(false);
      onDeleted && onDeleted();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const removeTech = async (force) => {
    try {
      await api.delete(`/technicians/${tech.id}${force ? "?force=true" : ""}`);
      toast.success("Tecnico eliminato");
      onClose();
      onDeleted && onDeleted();
    } catch (err) {
      if (err.response?.status === 409) {
        if (window.confirm(`${err.response.data.detail}\n\nVuoi eliminarlo comunque? Lo storico delle richieste resterà.`)) {
          removeTech(true);
        }
      } else {
        toast.error(formatApiError(err.response?.data?.detail));
      }
    }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-h-[92vh] overflow-y-auto sm:max-w-lg" data-testid="technician-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{tech.name}</DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">
            {tech.email}{tech.phone ? ` · ${tech.phone}` : ""}{tech.birthday ? ` · Compleanno ${formatDate(tech.birthday)}` : ""}
          </DialogDescription>
        </DialogHeader>

        {edit ? (
          <div className="space-y-3" data-testid="edit-technician-form">
            <div className="space-y-1.5"><Label>Nome</Label><Input data-testid="edit-tech-name-input" value={form.name} onChange={setField("name")} className="bg-slate-800 border-slate-700" /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input type="email" data-testid="edit-tech-email-input" value={form.email} onChange={setField("email")} className="bg-slate-800 border-slate-700" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Telefono</Label><Input data-testid="edit-tech-phone-input" value={form.phone} onChange={setField("phone")} placeholder="+39 ..." className="bg-slate-800 border-slate-700" /></div>
              <div className="space-y-1.5"><Label>Compleanno</Label><Input type="date" data-testid="edit-tech-birthday-input" value={form.birthday} onChange={setField("birthday")} className="bg-slate-800 border-slate-700" /></div>
            </div>
            <EmployeeToggle value={form.employee} onChange={(v) => setForm((f) => ({ ...f, employee: v }))} testid="edit-tech-employee-toggle" />
            <SubcontractorToggle value={form.subcontractor} onChange={(v) => setForm((f) => ({ ...f, subcontractor: v, employee: v || f.employee }))} testid="edit-tech-subcontractor-toggle" />
            <VideomakerToggle value={form.videomaker} onChange={(v) => setForm((f) => ({ ...f, videomaker: v }))} testid="edit-tech-videomaker-toggle" />
            <WarehouseToggle value={form.warehouse} onChange={(v) => setForm((f) => ({ ...f, warehouse: v }))} testid="edit-tech-warehouse-toggle" />
            <div className="space-y-1.5">
              <Label>Skills</Label>
              <div className="flex flex-wrap gap-2">
                {SKILLS.map((s) => {
                  const a = form.skills.includes(s.id);
                  return (
                    <button key={s.id} type="button" data-testid={`edit-tech-skill-${s.id}`} onClick={() => toggleSkill(s.id)}
                      className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-all ${a ? s.color : "border-slate-700 text-slate-400"}`}>
                      <s.icon className="w-3.5 h-3.5" /> {s.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <DialogFooter className="pt-2">
              <Button variant="outline" onClick={() => setEdit(false)} className="border-slate-700 text-slate-300">Annulla</Button>
              <Button onClick={saveEdit} disabled={saving} data-testid="save-edit-technician-btn" className="bg-cyan-500 hover:bg-cyan-600 text-slate-950 font-semibold">Salva modifiche</Button>
            </DialogFooter>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-2">
              <div className="flex flex-wrap gap-1.5">{tech.subcontractor ? <SubcontractorBadge /> : tech.employee && <EmployeeBadge />}{tech.videomaker && <VideomakerBadge />}{tech.warehouse && <WarehouseBadge />}{tech.skills?.map((s) => <SkillBadge key={s} id={s} />)}</div>
              <Button variant="outline" size="sm" data-testid="edit-technician-btn" onClick={startEdit}
                className="border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 shrink-0">
                <Pencil className="w-3.5 h-3.5 mr-1.5" /> Modifica
              </Button>
            </div>
            <UnavailSection techId={tech.id} items={unavail} onChanged={onUnavailChanged} />
            {requests.length === 0 ? (
              <p className="text-sm text-slate-500">Nessuna richiesta per questo tecnico.</p>
            ) : (
              groups.map((g) => {
                const items = requests.filter((r) => r.status === g.key);
                if (items.length === 0) return null;
                return (
                  <div key={g.key} className="pt-2">
                    <h4 className="font-semibold mb-2 flex items-center gap-2">{g.title} <span className="text-xs text-slate-500">({items.length})</span></h4>
                    <div className="space-y-2">
                      {items.map((r) => (
                        <div key={r.id} data-testid={`tech-detail-req-${r.id}`} className="flex items-center justify-between gap-2 rounded-lg border border-slate-800 bg-slate-900 p-2.5 flex-wrap">
                          <div className="min-w-0">
                            <p className="font-medium truncate">{r.event_name || r.title || "Disponibilità"}</p>
                            <p className="text-xs text-slate-500">{formatRange(r.date, r.date_end)}</p>
                          </div>
                          <div className="flex items-center gap-2"><SkillBadge id={r.skill} /><StatusBadge status={r.status} /></div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })
            )}
            <DialogFooter className="pt-2 border-t border-slate-800 flex-col sm:flex-row gap-2">
              {!tech.linked_manager && (
              <Button variant="outline" data-testid="detail-resend-password-btn"
                onClick={async () => {
                  if (!window.confirm(`Reimpostare la password di ${tech.name} e inviargliela via email?`)) return;
                  try {
                    const res = await api.post(`/technicians/${tech.id}/reset-password`);
                    if (res.data.email_sent) toast.success(`Nuova password inviata a ${tech.email}`);
                    else toast.warning("Password reimpostata, ma l'email non è partita.");
                  } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                }}
                className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10">
                <KeyRound className="w-4 h-4 mr-1.5" /> Reinvia password
              </Button>
              )}
              <Button variant="outline" data-testid="delete-technician-btn" onClick={() => removeTech(false)}
                className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10">
                <Trash2 className="w-4 h-4 mr-1.5" /> Elimina tecnico
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* --------------------------- Event detail ------------------------------- */
function DeleteEventConfirm({ step, setStep, event, pending, onConfirm, reason, setReason }) {
  const final = step === 2;
  return (
    <AlertDialog open={step > 0} onOpenChange={(o) => { if (!o) setStep(0); }}>
      <AlertDialogContent data-testid={`delete-event-step-${step}`} className="bg-slate-900 border-slate-800 text-slate-100">
        <AlertDialogHeader>
          <AlertDialogTitle className={final ? "text-rose-400" : ""}>
            {final ? "Questa azione è definitiva" : `Vuoi eliminare l'evento "${event.name}"?`}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-slate-400">
            {final
              ? (pending ? `Verranno annullate le richieste di ${pending} tecnic${pending > 1 ? "i" : "o"} e tutti riceveranno un avviso di evento cancellato.` : "L'evento verrà eliminato e non potrà essere recuperato.")
              : `Evento · ${formatRange(event.start_date, event.end_date)}${event.hide_name ? " · nome nascosto ai freelance" : ""}`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {final && pending > 0 && (
          <div className="space-y-1.5">
            <Label className="text-slate-300">Motivo della cancellazione <span className="text-slate-500 font-normal">(facoltativo, incluso nell'email ai tecnici)</span></Label>
            <Textarea data-testid="delete-event-reason" value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Es. il cliente ha annullato la data" className="bg-slate-800 border-slate-700" />
          </div>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="delete-event-cancel" className="bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800">Annulla</AlertDialogCancel>
          <Button data-testid={final ? "delete-event-final" : "delete-event-next"} onClick={() => (final ? onConfirm() : setStep(2))}
            className={final ? "bg-rose-600 hover:bg-rose-700 text-white" : "bg-rose-500/20 text-rose-200 hover:bg-rose-500/30"}>
            {final ? "Sì, elimina definitivamente" : "Elimina"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function TimeFields({ form, set, prefix }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-1.5">
        <Label>Orario inizio (opzionale)</Label>
        <Input type="time" data-testid={`${prefix}-start-time-input`} value={form.start_time} onChange={set("start_time")} className="bg-slate-800 border-slate-700" />
      </div>
      <div className="space-y-1.5">
        <Label>Orario fine (opzionale)</Label>
        <Input type="time" data-testid={`${prefix}-end-time-input`} value={form.end_time} onChange={set("end_time")} className="bg-slate-800 border-slate-700" />
      </div>
    </div>
  );
}

function StaffingStatus({ event, eventRequests }) {
  const s = parseDay(event.start_date), n = diffDays(s, parseDay(event.end_date || event.start_date)) + 1;
  const days = Array.from({ length: n }, (_, i) => dayKey(addDays(s, i)));
  const conf = (d, vm) => eventRequests.filter((r) => r.date === d && r.status === "confermato" && !r.supervisor && !r.warehouse && !!(r.videomaker && event.required_videomakers) === vm).length;
  const ok = days.every((d) => conf(d, false) >= (event.required_technicians || 0) && conf(d, true) >= (event.required_videomakers || 0));
  return (
    <div className="pt-2" data-testid="staffing-status">
      <span data-testid={ok ? "staffing-complete" : "staffing-incomplete"}
        className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-bold ${ok ? "bg-emerald-500 text-white" : "bg-red-600 text-white"}`}>
        {ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />} {ok ? "Completo" : "Incompleto"}
      </span>
    </div>
  );
}

export function DaySummary({ event, eventRequests }) {
  if (!event.start_date) return null;
  const s = parseDay(event.start_date);
  const n = diffDays(s, parseDay(event.end_date || event.start_date)) + 1;
  const need = event.required_technicians;
  const days = Array.from({ length: n }, (_, i) => dayKey(addDays(s, i)));
  return (
    <div className="pt-2" data-testid="day-summary">
      <h4 className="font-semibold mb-2 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-cyan-400" /> Riepilogo giorni</h4>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {days.map((d) => {
          const rs = eventRequests.filter((r) => r.date === d && !r.supervisor && !r.videomaker && !r.warehouse);
          const conf = rs.filter((r) => r.status === "confermato").length;
          const yes = rs.filter((r) => r.status === "disponibile").length;
          const gap = need != null ? need - conf : null;
          const tone = gap == null ? "border-slate-700 bg-slate-800/40" : gap > 0 ? "border-rose-500/50 bg-rose-500/10" : "border-emerald-500/50 bg-emerald-500/10";
          return (
            <div key={d} data-testid={`day-summary-${d}`} className={`rounded-lg border px-2.5 py-2 ${tone}`}>
              <p className="text-[11px] uppercase tracking-wide text-slate-400 capitalize">{dayLabel(d)}</p>
              <p className="text-lg font-bold text-white leading-tight" data-testid={`day-summary-count-${d}`}>{conf}{need != null && <span className="text-sm text-slate-400">/{need}</span>} <span className="text-xs font-medium text-slate-400">confermati</span></p>
              <p className="text-[11px] text-slate-400">
                {gap != null && gap > 0 ? <span className="text-rose-300 font-semibold">mancano {gap}</span> : gap != null ? <span className="text-emerald-300 font-semibold">completo</span> : null}
                {yes > 0 && <span className="text-emerald-300">{gap != null ? " · " : ""}{yes} disponibil{yes > 1 ? "i" : "e"} da confermare</span>}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SupervisorAdder({ event, technicians, unavMap, onDone }) {
  const s = parseDay(event.start_date);
  const days = Array.from({ length: diffDays(s, parseDay(event.end_date || event.start_date)) + 1 }, (_, i) => dayKey(addDays(s, i)));
  const [techId, setTechId] = useState("");
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  const tech = technicians.find((t) => t.id === techId);
  const add = async () => {
    setBusy(true);
    try {
      const body = { technician_ids: [techId], dates: sel, supervisor: true };
      const r = tech.employee ? await api.post(`/events/${event.id}/assign`, body) : await api.post("/requests/bulk", { ...body, event_id: event.id });
      if (r.data.created) toast.success(`${tech.name} aggiunto come supervisore`);
      else toast.error(`${tech.name} è già su questo evento o impegnato in quei giorni`);
      setTechId(""); setSel([]); onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="pt-2 border-t border-slate-800 space-y-2" data-testid="supervisor-section">
      <h4 className="font-semibold flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-violet-400" /> Aggiungi supervisore</h4>
      <p className="text-xs text-slate-500">Non conta nei tecnici previsti. Il freelance riceve subito la richiesta, il dipendente viene messo Disponibile.</p>
      <Select value={techId} onValueChange={setTechId}>
        <SelectTrigger data-testid="supervisor-tech-select" className="bg-slate-800 border-slate-700"><SelectValue placeholder="Scegli la persona" /></SelectTrigger>
        <SelectContent className="bg-slate-900 border-slate-700 text-slate-100 max-h-64">
          {technicians.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}{t.employee ? " (dipendente)" : ""}{unavMap[t.id]?.length ? " · non disponibile" : ""}</SelectItem>)}
        </SelectContent>
      </Select>
      <div className="flex flex-wrap gap-1.5" data-testid="supervisor-days">
        {days.map((d) => {
          const on = sel.includes(d);
          return (
            <button key={d} type="button" data-testid={`supervisor-day-${d}`} onClick={() => setSel((x) => (on ? x.filter((y) => y !== d) : [...x, d]))}
              className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${on ? "border-violet-500 bg-violet-500/15 text-violet-200" : "border-slate-700 text-slate-400 hover:border-slate-500"}`}>{dayLabel(d)}</button>
          );
        })}
        {days.length > 1 && <button type="button" data-testid="supervisor-all-days" onClick={() => setSel(sel.length === days.length ? [] : days)} className="text-xs text-cyan-400 hover:underline px-1">{sel.length === days.length ? "Nessuno" : "Tutti"}</button>}
      </div>
      <Button onClick={add} disabled={busy || !techId || !sel.length} data-testid="supervisor-add-btn" className="bg-violet-500 hover:bg-violet-600 text-white font-semibold">
        <Plus className="w-4 h-4 mr-1.5" /> Aggiungi supervisore{sel.length ? ` (${sel.length} giorn${sel.length > 1 ? "i" : "o"})` : ""}
      </Button>
    </div>
  );
}

function EventDetailDialog({ event, eventRequests, allRequests, unavail = [], technicians, events, onOpenTech, onClose, onDone, onConfirmService, onClientConfirm }) {
  const { categories } = useCategories();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    name: event.name, client_name: event.client_name || "",
    start_date: event.start_date, end_date: event.end_date,
    location: event.location || "", notes: event.notes || "",
    start_time: event.start_time || "", end_time: event.end_time || "",
    category: event.category || "none",
    required_technicians: event.required_technicians ? String(event.required_technicians) : "none",
    required_videomakers: String(event.required_videomakers || 0),
    hide_name: !!event.hide_name,
  });
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [addSkill, setAddSkill] = useState("all");
  const [addSelected, setAddSelected] = useState([]);
  const [vmSelected, setVmSelected] = useState([]);
  const [adding, setAdding] = useState(false);
  const fileRef = useRef(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const token = localStorage.getItem("sc_token");

  const loadAttachments = async () => {
    try {
      const res = await api.get(`/events/${event.id}/attachments`);
      setAttachments(res.data);
    } catch (err) { /* noop */ }
  };
  useEffect(() => { loadAttachments(); /* eslint-disable-next-line */ }, [event.id]);

  const saveEvent = async () => {
    if (!form.name) { toast.error("Il nome non può essere vuoto"); return; }
    if (form.end_date && form.end_date < form.start_date) { toast.error("La data di fine non può precedere l'inizio"); return; }
    try {
      await api.put(`/events/${event.id}`, { ...form, start_time: form.start_time || null, end_time: form.end_time || null, category: form.category === "none" ? null : form.category, required_technicians: form.required_technicians === "none" ? null : Number(form.required_technicians), required_videomakers: Number(form.required_videomakers || 0) });
      toast.success("Evento aggiornato");
      setEditing(false);
      onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const [moveNew, setMoveNew] = useState(null);
  const removeTech = async (tid) => {
    try {
      await api.delete(`/events/${event.id}/technicians/${tid}`);
      toast.success("Tecnico rimosso e notificato");
      onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remindRequest = async (id) => {
    try {
      await api.post(`/requests/${id}/remind`);
      toast.success("Sollecito inviato");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const contactedIds = new Set(eventRequests.map((r) => r.technician_id));
  const notContacted = technicians.filter((t) => !contactedIds.has(t.id) && !t.employee);
  const freeEmployees = technicians.filter((t) => !contactedIds.has(t.id) && t.employee);
  const [empSelected, setEmpSelected] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const assignEmployees = async () => {
    setAssigning(true);
    try {
      const res = await api.post(`/events/${event.id}/assign`, { technician_ids: empSelected });
      const { created, skipped } = res.data;
      if (created > 0) toast.success(`${created} dipendente/i assegnato/i come disponibili${skipped ? ` (${skipped} già impegnati in quelle date)` : ""}`);
      else toast.error("Nessuno assegnato: già impegnati in quelle date");
      setEmpSelected([]);
      onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setAssigning(false); }
  };
  const evEnd = event.end_date || event.start_date;
  const conflicts = useMemo(() => {
    const m = {};
    (allRequests || []).forEach((o) => {
      if (o.event_id === event.id || !o.date) return;
      if (o.date < event.start_date || o.date > evEnd) return;
      (m[o.technician_id] = m[o.technician_id] || []).push(o);
    });
    Object.values(m).forEach((l) => l.sort((a, b) => a.date.localeCompare(b.date)));
    return m;
  }, [allRequests, event.id, event.start_date, evEnd]);
  const evDaysN = diffDays(parseDay(event.start_date), parseDay(evEnd)) + 1;
  const blockedDays = (id) => {
    const s = new Set((conflicts[id] || []).map((o) => o.date));
    for (let i = 0; i < evDaysN; i++) { const d = dayKey(addDays(parseDay(event.start_date), i)); if (unavail.some((u) => u.tech_id === id && u.start <= d && u.end >= d)) s.add(d); }
    return s.size;
  };
  const isRental = /noleggi/i.test(event.category || "");
  const isVmCat = /videomaker/i.test(event.category || "");
  const pickableAll = notContacted.filter((t) => blockedDays(t.id) < evDaysN);
  const pickable = pickableAll.filter((t) => !t.videomaker && !t.warehouse);
  const pickableVm = pickableAll.filter((t) => t.videomaker);
  const assignableEmployees = freeEmployees.filter((t) => blockedDays(t.id) < evDaysN);
  const freePerDay = Array.from({ length: evDaysN }, (_, i) => {
    const d = dayKey(addDays(parseDay(event.start_date), i));
    const n = technicians.filter((t) => !t.employee && !t.videomaker && !t.warehouse && (addSkill === "all" || (t.skills || []).includes(addSkill))
      && !(allRequests || []).some((o) => o.technician_id === t.id && o.date === d)
      && !unavail.some((u) => u.tech_id === t.id && u.start <= d && u.end >= d)).length;
    return { d, n };
  });
  const selectedConflicts = (addSelected.length ? addSelected : pickable.map((t) => t.id)).filter((id) => conflicts[id]?.length);
  const unavMap = useMemo(() => {
    const m = {};
    unavail.forEach((u) => { if (u.start <= evEnd && u.end >= event.start_date) (m[u.tech_id] = m[u.tech_id] || []).push(u); });
    return m;
  }, [unavail, event.start_date, evEnd]);

  const addTechnicians = async (vm = false) => {
    setAdding(true);
    try {
      const res = await api.post("/requests/bulk", {
        event_id: event.id, skill: vm || addSkill === "all" ? null : addSkill, strict: true,
        technician_ids: vm ? (vmSelected.length ? vmSelected : pickableVm.map((t) => t.id))
          : addSelected.length ? addSelected : pickable.filter((t) => addSkill === "all" || (t.skills || []).includes(addSkill)).map((t) => t.id),
      });
      const { created, skipped } = res.data;
      const who = vm ? "videomaker" : "tecnico/i";
      if (created > 0) toast.success(`${created} ${who} interpellato/i${skipped ? ` (${skipped} già occupati in quelle date)` : ""}`);
      else toast.error("Nessuna nuova richiesta: sono già interpellati o non disponibili in quelle date");
      if (created > 0 && !vm && selectedConflicts.length) toast.warning("Le date in cui il tecnico è già interpellato o non disponibile non sono state richieste");
      setAddSelected([]); setVmSelected([]);
      onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setAdding(false); }
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/events/${event.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Allegato caricato");
      loadAttachments();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeAttachment = async (id) => {
    try {
      await api.delete(`/attachments/${id}`);
      toast.success("Allegato eliminato");
      loadAttachments();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const [delStep, setDelStep] = useState(0);
  const [delReason, setDelReason] = useState("");
  const deleteEvent = async () => {
    try {
      const res = await api.delete(`/events/${event.id}`, { params: { force: true, reason: delReason.trim() || undefined } });
      const n = res.data.notified || 0;
      toast.success(n ? `Evento eliminato · ${n} tecnic${n > 1 ? "i avvisati" : "o avvisato"}` : "Evento eliminato");
      setDelStep(0);
      setDelReason("");
      onClose();
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-h-[92vh] overflow-y-auto sm:max-w-2xl" data-testid="event-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            {editing ? "Modifica evento" : event.name}
            {!editing && (
              <button data-testid="edit-event-btn" onClick={() => setEditing(true)} className="text-slate-400 hover:text-cyan-400">
                <Pencil className="w-4 h-4" />
              </button>
            )}
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">Gestisci tecnici, allegati e dettagli dell'evento.</DialogDescription>
        </DialogHeader>

        {/* Event info / edit */}
        {editing ? (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Nome evento</Label>
              <Input data-testid="edit-event-name-input" value={form.name} onChange={set("name")} className="bg-slate-800 border-slate-700" />
              <HideNameToggle value={form.hide_name} onChange={(v) => setForm((f) => ({ ...f, hide_name: v }))} testid="edit-event-hide-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Cliente</Label>
              <Input value={form.client_name} onChange={set("client_name")} className="bg-slate-800 border-slate-700" />
            </div>
            <div className="space-y-1.5">
              <Label>Categoria</Label>
              <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="edit-event-category-select" className="bg-slate-800 border-slate-700"><SelectValue placeholder="Categoria" /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                  <SelectItem value="none">Nessuna</SelectItem>
                  {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Tecnici richiesti</Label>
              <Select value={form.required_technicians} onValueChange={(v) => setForm((f) => ({ ...f, required_technicians: v }))}>
                <SelectTrigger data-testid="edit-event-required-select" className="bg-slate-800 border-slate-700"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-100 max-h-60">
                  <SelectItem value="none">Non specificato</SelectItem>
                  {Array.from({ length: 20 }, (_, i) => i + 1).map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <VideomakerCountSelect value={form.required_videomakers} onChange={(v) => setForm((f) => ({ ...f, required_videomakers: v }))} testid="edit-event-videomakers-select" />
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Dal</Label>
                <Input type="date" value={form.start_date} onChange={set("start_date")} className="bg-slate-800 border-slate-700" />
              </div>
              <div className="space-y-1.5">
                <Label>Al</Label>
                <Input type="date" value={form.end_date} onChange={set("end_date")} min={form.start_date || undefined} className="bg-slate-800 border-slate-700" />
              </div>
            </div>
            <TimeFields form={form} set={set} prefix="edit-event" />
            <div className="space-y-1.5">
              <Label>Luogo</Label>
              <Input value={form.location} onChange={set("location")} className="bg-slate-800 border-slate-700" />
            </div>
            <div className="space-y-1.5">
              <Label>Note logistica <span className="text-slate-500 font-normal">(visibili ai tecnici confermati)</span></Label>
              <Textarea data-testid="edit-event-notes-input" value={form.notes} onChange={set("notes")} className="bg-slate-800 border-slate-700 min-h-[90px]" />
            </div>
            <div className="flex gap-2">
              <Button onClick={saveEvent} data-testid="save-event-edit-btn" className="bg-cyan-500 hover:bg-cyan-600 text-slate-950 font-semibold">Salva</Button>
              <Button variant="outline" onClick={() => setEditing(false)} className="border-slate-700">Annulla</Button>
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-slate-800 bg-slate-800/40 p-3 text-sm space-y-1">
            {event.client_name && <p className="text-slate-300">Cliente: <span className="text-white">{event.client_name}</span></p>}
            {event.category && <div><CategoryBadge id={event.category} /></div>}
            <p className="flex items-center gap-1.5 text-slate-300"><CalendarDays className="w-4 h-4 text-cyan-400" /> {formatRange(event.start_date, event.end_date)}</p>
            <HideNameToggle value={!!event.hide_name} testid="detail-hide-name-toggle" onChange={async (v) => {
              try { await api.put(`/events/${event.id}`, { hide_name: v }); toast.success(v ? "Nome nascosto ai freelance" : "Nome visibile ai freelance"); onDone(); }
              catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
            }} />
            {event.required_technicians != null && (() => {
              const avail = techCount(eventRequests.filter((r) => !r.supervisor && !(r.videomaker && event.required_videomakers) && !r.warehouse && (r.status === "disponibile" || r.status === "confermato")));
              const miss = event.required_technicians - avail;
              return (
                <p data-testid="event-staffing" className="flex items-center gap-1.5 text-slate-300">
                  <Users className="w-4 h-4 text-cyan-400" /> Tecnici: {avail}/{event.required_technicians} disponibili{" "}
                  {miss > 0 ? <span className="text-amber-300">(mancano {miss})</span> : <span className="text-emerald-300">(completo)</span>}
                </p>
              );
            })()}
            {!!event.required_videomakers && (() => {
              const v = techCount(eventRequests.filter((r) => r.videomaker && !r.supervisor && (r.status === "disponibile" || r.status === "confermato")));
              const miss = event.required_videomakers - v;
              return (
                <p data-testid="event-videomaker-staffing" className="flex items-center gap-1.5 text-slate-300">
                  <Users className="w-4 h-4 text-pink-400" /> Videomaker: {v}/{event.required_videomakers} disponibili{" "}
                  {miss > 0 ? <span className="text-amber-300">(mancano {miss})</span> : <span className="text-emerald-300">(completo)</span>}
                </p>
              );
            })()}
            {(event.start_time || event.end_time) && <p className="flex items-center gap-1.5 text-slate-300" data-testid="event-detail-times"><Clock className="w-4 h-4" /> {event.start_time || "?"}{event.end_time ? ` – ${event.end_time}` : ""}</p>}
            {event.location && <p className="flex items-center gap-1.5 text-slate-300"><MapPin className="w-4 h-4" /> {event.location}</p>}
            {event.notes && <div data-testid="event-detail-notes"><p className="text-xs uppercase tracking-wider font-mono text-slate-500">Note logistica</p><p className="text-slate-300 whitespace-pre-wrap">{event.notes}</p></div>}
            {!event.client_confirmed && (
              <Button size="sm" variant="outline" data-testid="detail-client-confirm-btn"
                onClick={() => onClientConfirm(event.id)} className="mt-2 border-slate-700 hover:bg-slate-800 text-slate-200">
                <CheckCircle2 className="w-4 h-4 mr-1.5" /> Segna conferma cliente
              </Button>
            )}
          </div>
        )}

        {isRental ? (
          <p data-testid="rental-detail-note" className="pt-2 text-sm text-slate-400">Noleggio: non servono tecnici né videomaker.</p>
        ) : (<>
        <StaffingStatus event={event} eventRequests={eventRequests} />

        {!isVmCat && (
        <div className="pt-2 border-t border-slate-800" data-testid="add-technicians-section">
          <h4 className="font-semibold mb-2 flex items-center gap-2"><UserPlus className="w-4 h-4 text-cyan-400" /> Aggiungi tecnici</h4>
          <p className="text-xs text-slate-500 mb-2">Scegli chi interpellare. Non compaiono i già interpellati per questo evento e chi in tutte le date è già interpellato su altri eventi (in attesa, forse, disponibile, non disponibile, confermato) o segnato non disponibile.</p>
          <div className="flex flex-wrap gap-1.5 mb-2" data-testid="free-per-day">
            {freePerDay.map(({ d, n }) => (
              <span key={d} data-testid={`free-count-${d}`} className={`rounded-md border px-2 py-1 text-[11px] font-medium ${n ? "border-lime-400/40 bg-lime-400/10 text-lime-300" : "border-rose-500/40 bg-rose-500/10 text-rose-300"}`}>
                <span className="capitalize">{dayLabel(d)}</span>: <b>{n}</b> liber{n === 1 ? "o" : "i"}
              </span>
            ))}
          </div>
          <div className="flex items-center gap-2 mb-2">
            <Select value={addSkill} onValueChange={(v) => { setAddSkill(v); setAddSelected([]); }}>
              <SelectTrigger data-testid="add-skill-select" className="w-[180px] bg-slate-800 border-slate-700"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                <SelectItem value="all">Tutte le skill</SelectItem>
                {SKILLS.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={() => addTechnicians(false)} disabled={adding || pickable.length === 0} data-testid="add-technicians-btn" className="bg-cyan-500 hover:bg-cyan-600 text-slate-950 font-semibold">
              <Send className="w-4 h-4 mr-1.5" /> Interpella {addSelected.length > 0 ? `(${addSelected.length})` : "tutti"}
            </Button>
          </div>
          {pickable.length === 0 ? (
            <p className="text-sm text-slate-500" data-testid="no-pickable-msg">Nessun tecnico da interpellare.</p>
          ) : (
            <TechnicianPicker technicians={pickable} skill={addSkill} selected={addSelected} setSelected={setAddSelected} conflicts={conflicts} unavailable={unavMap} />
          )}
          {selectedConflicts.length > 0 && (
            <div data-testid="conflict-summary" className="mt-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-2.5 text-xs text-amber-200 flex gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0 text-amber-400" />
              <span>{selectedConflicts.map((id) => technicians.find((t) => t.id === id)?.name).filter(Boolean).join(", ")}: hanno già richieste su altri eventi o sono non disponibili in alcune date. Per quelle date non verrà chiesta; per le altre sì.</span>
            </div>
          )}
        </div>
        )}

        <div className="pt-2 border-t border-slate-800" data-testid="add-videomakers-section">
          <h4 className="font-semibold mb-2 flex items-center gap-2"><UserPlus className="w-4 h-4 text-pink-400" /> Aggiungi videomaker</h4>
          <p className="text-xs text-slate-500 mb-2">Solo per i lavori che li richiedono. Non contano nei tecnici previsti. Stesse esclusioni dei tecnici.</p>
          <div className="flex items-center gap-2 mb-2">
            <Button onClick={() => addTechnicians(true)} disabled={adding || pickableVm.length === 0} data-testid="add-videomakers-btn" className="bg-pink-500 hover:bg-pink-600 text-white font-semibold">
              <Send className="w-4 h-4 mr-1.5" /> Interpella {vmSelected.length > 0 ? `(${vmSelected.length})` : "tutti"}
            </Button>
          </div>
          {pickableVm.length === 0 ? (
            <p className="text-sm text-slate-500" data-testid="no-pickable-vm-msg">Nessun videomaker da interpellare.</p>
          ) : (
            <TechnicianPicker technicians={pickableVm} skill="all" selected={vmSelected} setSelected={setVmSelected} conflicts={conflicts} unavailable={unavMap} />
          )}
        </div>


        </>)}

        {/* Assign employees */}
        <SubcontractorMails event={event} eventRequests={eventRequests} technicians={technicians} />
        <div className="pt-2 border-t border-slate-800" data-testid="assign-employees-section">
          <div className="flex items-center justify-between gap-2 mb-2">
            <h4 className="font-semibold flex items-center gap-2"><UserCheck className="w-4 h-4 text-amber-400" /> Assegna dipendenti e subappaltatori</h4>
            <Button onClick={assignEmployees} disabled={assigning || empSelected.length === 0} data-testid="assign-employees-btn" className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold">
              <UserCheck className="w-4 h-4 mr-1.5" /> Assegna {empSelected.length > 0 ? `(${empSelected.length})` : ""}
            </Button>
          </div>
          <p className="text-xs text-slate-500 mb-2">Li inserisci tu come Disponibili, senza inviare richieste. I dipendenti ricevono la mail solo quando li confermi; ai subappaltatori scrivi tu.</p>
          {assignableEmployees.length === 0 ? (
            <p className="text-sm text-slate-500" data-testid="no-employees-msg">Nessun dipendente da assegnare: sono già assegnati a un servizio in tutte le date o non ce ne sono (Tecnici → Modifica → Dipendente).</p>
          ) : (
            <TechnicianPicker technicians={assignableEmployees} skill="all" selected={empSelected} setSelected={setEmpSelected} conflicts={conflicts} unavailable={unavMap} />
          )}
        </div>

        {/* Attachments */}
        <div className="pt-2 border-t border-slate-800">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-semibold flex items-center gap-2"><Paperclip className="w-4 h-4 text-cyan-400" /> Allegati</h4>
            <input ref={fileRef} type="file" className="hidden" onChange={onPickFile} data-testid="attachment-file-input" />
            <Button size="sm" variant="outline" disabled={uploading} data-testid="upload-attachment-btn"
              onClick={() => fileRef.current?.click()} className="border-slate-700 hover:bg-slate-800">
              <Upload className="w-4 h-4 mr-1.5" /> {uploading ? "Caricamento..." : "Carica file"}
            </Button>
          </div>
          {attachments.length === 0 ? (
            <p className="text-sm text-slate-500">Nessun allegato.</p>
          ) : (
            <div className="space-y-2">
              {attachments.map((a) => (
                <div key={a.id} data-testid={`attachment-${a.id}`} className="flex items-center justify-between gap-2 rounded-lg border border-slate-800 bg-slate-900 p-2.5">
                  <a href={`${API}/attachments/${a.id}/download?auth=${token}`} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 min-w-0 text-cyan-300 hover:underline">
                    <Download className="w-4 h-4 shrink-0" />
                    <span className="truncate text-sm">{a.original_filename}</span>
                  </a>
                  <Button size="sm" variant="outline" data-testid={`remove-attachment-${a.id}`} onClick={() => removeAttachment(a.id)}
                    className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10 h-8">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Danger zone */}
        <div className="pt-2 border-t border-slate-800">
          <Button variant="outline" data-testid="delete-event-btn" onClick={() => setDelStep(1)}
            className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10">
            <Trash2 className="w-4 h-4 mr-1.5" /> Elimina evento
          </Button>
        </div>
        <DeleteEventConfirm step={delStep} setStep={setDelStep} event={event} reason={delReason} setReason={setDelReason}
          pending={techCount(eventRequests || [])} onConfirm={deleteEvent} />
        <NewEventDialog open={!!moveNew} onOpenChange={(o) => { if (!o) setMoveNew(null); }}
          defaultDate={moveNew ? { start: moveNew.date, end: moveNew.date } : undefined}
          onDone={async (ev) => {
            const req = moveNew;
            if (ev && req) {
              try { await api.put(`/requests/${req.id}/move`, { event_id: ev.id }); toast.success("Tecnico spostato nel nuovo evento"); }
              catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
            }
            onDone();
          }} />
      </DialogContent>
    </Dialog>
  );
}
